import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom"; // Add this line
import { useTheme } from "../contexts/ThemeContext";
import { db } from "../firebase";
import { doc, onSnapshot, getDoc, setDoc, updateDoc, collection, writeBatch, getDocs, addDoc, deleteField, getFirestore, query, where } from "firebase/firestore";
import { getSettings, updateSettings, getNextTradeNumber } from "../services/FirestoreService";
import Papa from "papaparse";
import { FaUpload, FaSave, FaSync, FaExclamationTriangle,FaChevronDown, FaCalculator } from "react-icons/fa";
import {Settings as SettingsIcon} from "lucide-react";
import CollapsibleCard from "../components/CollapsibleCard";
import { useAuth } from "../contexts/GoogleAuthContext";
import SimpleCalculator from '../components/SimpleCalculator'; 





import { FOREX_PAIRS, FOREX_INDICES, FUTURES_PRODUCTS, futuresCodeOf } from "../constants/instruments";


const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

const InfoTooltip = ({ content }) => {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative inline-block ml-2">
      <button
        type="button"
        className="text-gray-400 hover:text-blue-500 transition-colors focus:outline-none align-middle"
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        onClick={() => setVisible(!visible)}
        aria-label="Info"
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
      </button>
      {visible && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 w-72 p-4 bg-blue-600 text-white text-sm font-medium rounded-xl shadow-2xl z-50 text-left leading-relaxed border border-blue-500">
          {content}
          {/* Arrow */}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-3 h-3 bg-blue-600 rotate-45 -mt-1.5 shadow-sm"></div>
        </div>
      )}
    </div>
  );
};



// --- Helper Functions ---
async function getUserSettings(userId) {
  if (!userId) return null;

  const userSettingsRef = doc(
    db,
    "users",
    userId,
    "settings",
    "userSettings"
  );

  const docSnap = await getDoc(userSettingsRef);

  return docSnap.exists()
    ? docSnap.data()
    : null;
}

async function updateUserSettings(userId, settings) {
  if (!userId) return;

  const userSettingsRef = doc(
    db,
    "users",
    userId,
    "settings",
    "userSettings"
  );

  await setDoc(
    userSettingsRef,
    settings,
    { merge: true }
  );
}

async function getAccountSettings(userId, accountId) {
  if (!userId || !accountId) return null;
  const accountRef = doc(db, "users", userId, "accounts", accountId);
  const docSnap = await getDoc(accountRef);
  return docSnap.exists() ? docSnap.data() : null;
}
async function updateAccountSettings(userId, accountId, dataToUpdate) {
  if (!userId || !accountId) return;
  const accountRef = doc(db, "users", userId, "accounts", accountId);
  await updateDoc(accountRef, dataToUpdate);
}
async function appendTrade(userId, accountId, tradeData) {
    if (!userId || !accountId) return;
    const journalRef = collection(db, "users", userId, "accounts", accountId, "journal");
    await addDoc(journalRef, tradeData);
}
async function fetchJournalRows(userId, accountId) {
    if (!userId || !accountId) return [];
    const journalRef = collection(db, "users", userId, "accounts", accountId, "journal");
    const snapshot = await getDocs(journalRef);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

function formatCurrency(val, currency = "USD") {
  const n = Number(val);
  if (!isFinite(n)) return "$0.00";
  return n.toLocaleString("en-US", { style: "currency", currency, minimumFractionDigits: 2 });
}

const defaultSettings = {
  "Theme": "Dark", "Pretrade Checklist": "Yes", "Calculator Only": false,
  "Daily Goal": 500, "Daily Loss Limit": 200, "Max Trades Per Day": 10,
  "Max Loss Trades Per Day": 5, "Drawdown Limit %": 20, "Max Lot Size": 20,"Privacy Mode": false,
  "Min Lot Size": 0.01, "Account Currency": "USD", "Leverage": 100,
  "Calculation by Entry Price": false, "Max Trades Per Day Toggle": false,
  "Max Loss Trades Per Day Toggle": false, "Drawdown Limit % Toggle": true, "Maximum Contracts": 50,
  "Minimum Contracts":1,
  "Maximum Shares": 100,
  "Minimum Shares": 0.0001,
  "Point Goal":"",
  "EOD Reminders": false,
  "Reminder Method": "Email",
  "SMS Consent": false,
  "SMS Consent Timestamp": "",
  "SMS Consent Disclosure Version": "",
  "SMS Consent Phone Number": "",
  "SMS Opt Out Timestamp": "",
  "OvertradingPopupShownDate": "", "DrawdownPopupShownDate": "", "TooManyLossesPopupShownDate": "",
  "DailyGoalPopupShownDate": "", "DailyGoalPopupShownForGoal":"","LossLimitWarningShownDate": "",
  "MoodVolatilityPopupShownDate": "", "OverRiskWarningShownDate": "", "ConsecutiveWinsPopupShownDate": "",
  "AccountGoalReachedShownDate": "", "LessonsLearnedStreakWarningShownDate": "","RiskOfRuinWarningShownDate": "",
  "TwoLossWarningShownDate": "", "ThreeLossWarningShownDate": "",
  "Copy Role": "None",            // "None" | "Leader" | "Follower"
  "Copy Followers": [],           // array of accountIds (only used when Role=Leader)
  "Copy Leader Id": "",  
  "Default Timeframe": "",
};

const PREFS_KEYS = new Set([
  "Theme",
  "Pretrade Checklist",
  "PretradeChecklistShownDate",
  "hasSeenWelcomeVideo",
  "hasSeenWhatsNew_v4",
  "pretradeChecklistLastShown",
  "Privacy Mode"
]);

const USER_LEVEL_KEYS = new Set([
  "Privacy Mode",
  "EOD Reminders",
  "Reminder Method",
  "SMS Consent",
  "SMS Consent Timestamp",
  "SMS Consent Disclosure Version",
  "SMS Consent Phone Number",
  "SMS Opt Out Timestamp"
]);


const PLATFORMS = [
  { value: "topstep", label: "TopStepX" },
  { value: "tradovate", label: "Tradovate" },
  { value: "mannysgooglesheet", label: "Manny's Google Sheet" },
  { value: "trapound", label: "Trapound" },
  { value: "mt5", label: "MetaTrader 5 (CSV or XLSX)" },
];

const CURRENCY_OPTIONS = ["USD", "EUR", "GBP", "JPY", "AUD"];
const LEVERAGE_OPTIONS = [50, 100, 200, 500];
const TIMEFRAME_OPTIONS = ["30s","1m","2m","3m","5m","10m","15m","30m","1h","2h","4h","D","W","M"];

 const parseSheetNumber = (value) => {
   if (typeof value === "number") return value;
   if (value == null) return 0;
   const s = String(value).trim();
   // Treat "(123.45)" or "-123.45" as negative
   const isNegative = /^\(.*\)$/.test(s) || /^-/.test(s);
   const n = parseFloat(s.replace(/[^\d.]/g, "")) || 0;
   return isNegative ? -Math.abs(n) : n;
 };



// Searches every cell for the label and returns the value from the cell to its right.
function findValueByLabel(sheetData, label) {
  const lowerCaseLabel = label.toLowerCase();
  for (const row of sheetData) {
    if (!row) continue;
    for (let i = 0; i < row.length - 1; i++) {
      const cell = row[i];
      if (typeof cell === 'string' && cell.toLowerCase().includes(lowerCaseLabel)) {
        return row[i + 1]; // Return the value from the next cell to the right
      }
    }
  }
  return null; // Return null if not found anywhere
}

function formatDate(dateString) {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function Toggle({ label, checked, onChange }) {
  return (
    <div className="flex items-center justify-between py-2 mb-2">
      <span className="text-base font-semibold text-gray-800 dark:text-gray-100">{label}</span>
      <button
        onClick={() => onChange(!checked)}
        className={`relative w-14 h-7 flex items-center rounded-full p-1 transition-colors duration-300 ${
          checked ? "bg-blue-600" : "bg-gray-300"
        }`}
        aria-pressed={checked} >
        <span
          className={`bg-white w-6 h-6 rounded-full shadow-md transform transition-transform duration-300 ${
            checked ? "translate-x-7" : "translate-x-0"
          }`} />
      </button>
    </div>
  );
}

function toAmPm(timeStr) {
  if (!timeStr) return "";
  const [hour, min] = timeStr.split(":").map(x => parseInt(x, 10));
  if (isNaN(hour) || isNaN(min)) return "";
  const ampm = hour >= 12 ? "PM" : "AM";
  const formattedHour = hour % 12 || 12;
  return `${formattedHour}:${min.toString().padStart(2, "0")} ${ampm}`;
}

function splitDateTime(str) {
  if (!str) return { date: "", time: "" };
  const parts = str.split(" ");
  if (parts.length < 2) return { date: str, time: "" };
  return { date: parts[0], time: parts[1] };
}

function calculateDuration(start, end) {
  if (!start || !end) return "";
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (isNaN(startDate) || isNaN(endDate)) return "";
  
  let diffInMinutes = Math.abs(endDate - startDate) / (1000 * 60);
  const hours = Math.floor(diffInMinutes / 60);
  const minutes = Math.floor(diffInMinutes % 60);
  const seconds = Math.round((diffInMinutes % 1) * 60);

  return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}


async function setPretradeChecklist(uid, on) {
  const db = getFirestore();
  await setDoc(
    doc(db, "users", uid, "settings", "prefs"),
    { "Pretrade Checklist": on ? "Yes" : "No" },
    { merge: true }
  );
}

async function getUserPrefs(userId) {
  if (!userId) return null;
  const prefsRef = doc(db, "users", userId, "settings", "prefs");
  const docSnap = await getDoc(prefsRef);
  return docSnap.exists() ? docSnap.data() : null;
}

async function updateUserPrefsDoc(userId, settings) {
  if (!userId) return;
  const prefsRef = doc(db, "users", userId, "settings", "prefs");
  await setDoc(prefsRef, settings, { merge: true });
}

export async function updateUserPrefs(
  userId,
  patch
) {
  await updateUserSettings(userId, patch);
}

// (place near other firestore helpers)
async function listUserAccounts(userId) {
  const snap = await getDocs(collection(db, "users", userId, "accounts"));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

function guessAccountType(settings) {
  const s = settings || {};
  if (s?.settings?.CopyTrade?.accountType) return s.settings.CopyTrade.accountType;
  const sym = s?.settings?.Symbol ?? s?.Symbol;
  const prod = s?.settings?.Product ?? s?.Product;
  return sym ? "forex" : (prod ? "futures" : "futures"); // default to futures
}



// --- Helper Component for Config Rows ---
function FieldConfigRow({ 
  label, 
  defaultKey, 
  hideKey, 
  settings, 
  onChange, 
  showDefaultInput = true, 
  showHideToggle = true,
  list = null,
  options = null 
}) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700 last:border-0">
      <div className="flex items-center gap-2 flex-1">
        <span className="font-medium text-gray-700 dark:text-gray-200">{label}</span>
      </div>
      
      <div className="flex items-center gap-4">
        {showDefaultInput && (
          options ? (
            <select
              value={settings[defaultKey] || ""}
              onChange={(e) => onChange(defaultKey, e.target.value)}
              // ✅ Logic Change: If no value is selected, use gray text (placeholder style). 
              //    If a value IS selected, use standard dark/light text.
              className={`w-40 px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 focus:ring-2 focus:ring-blue-400 ${
                settings[defaultKey] 
                  ? "text-gray-900 dark:text-white" 
                  : "text-gray-500 dark:text-gray-400"
              }`}
            >
              <option value="">Select Default</option>
              {options.map(opt => (
                <option key={opt} value={opt} className="text-gray-900 dark:text-white">
                  {opt}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              list={list}
              placeholder="Default Value"
              value={settings[defaultKey] || ""}
              onChange={(e) => onChange(defaultKey, e.target.value)}
              className="w-40 px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-400"
            />
          )
        )}
        
        {showHideToggle && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {settings[hideKey] ? "Hidden" : "Visible"}
            </span>
            <Toggle
              label=""
              checked={!settings[hideKey]}
              onChange={(val) => onChange(hideKey, !val)}
            />
          </div>
        )}
      </div>
    </div>
  );
}


export default function Settings({ userId, accountId }) {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(defaultSettings);
  const [importOpen, setImportOpen] = useState(false);
  const [fundOpen, setFundOpen] = useState(false); // NEW: controls collapse for Account Funding
  const [platform, setPlatform] = useState("");
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importLog, setImportLog] = useState([]);
  const [importDuplicates, setImportDuplicates] = useState([]);
  const [finalBalance, setFinalBalance] = useState("");
  const [googleSheetUrl, setGoogleSheetUrl] = useState("");
  const [accountsList, setAccountsList] = useState([]);
  const acctType = guessAccountType({ settings });
  const copyRole = settings?.CopyTrade?.role || "off";
  const copyLeaderId = settings?.CopyTrade?.leaderId || "";
  const [allAccounts, setAllAccounts] = useState([]);
  const [futuresAccounts, setFuturesAccounts] = useState([]);
  const [leaderId, setLeaderId] = useState("");
  const [followerIds, setFollowerIds] = useState(new Set());
  const [forexAccounts, setForexAccounts] = useState([]);
  const [fxLeaderId, setFxLeaderId] = useState("");
  const [fxFollowerIds, setFxFollowerIds] = useState(new Set());
  const [savingCopyTrade, setSavingCopyTrade] = useState(false);
  const [copyTradeMsg, setCopyTradeMsg] = useState("");
  const [copyEnabledFutures, setCopyEnabledFutures] = useState(false);
  const [copyEnabledForex, setCopyEnabledForex] = useState(false);
  const [faveForex, setFaveForex] = useState(new Set());
  const [faveFutures, setFaveFutures] = useState(new Set());
  const [savingFavorites, setSavingFavorites] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const [favoritesMsg, setFavoritesMsg] = useState("");
  const [favoritesOpen, setFavoritesOpen] = React.useState(() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem("tp:favoritesOpen") : null;
    return saved === null ? false : saved === "1"; // ← default to false (collapsed)
  });
  const hasMultipleFutures = futuresAccounts?.length >= 2;
  const hasMultipleForex   = forexAccounts?.length >= 2;
  // Collapsible state (persisted) for Copy Trade cards
  const [copyForexOpen, setCopyForexOpen] = React.useState(() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem("tp:copyForexOpen") : null;
    return saved === null ? false : saved === "1"; // default collapsed
  });
  React.useEffect(() => {
    try { localStorage.setItem("tp:copyForexOpen", copyForexOpen ? "1" : "0"); } catch {}
  }, [copyForexOpen]);

  const [copyFuturesOpen, setCopyFuturesOpen] = React.useState(() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem("tp:copyFuturesOpen") : null;
    return saved === null ? false : saved === "1"; // default collapsed
  });
  React.useEffect(() => {
    try { localStorage.setItem("tp:copyFuturesOpen", copyFuturesOpen ? "1" : "0"); } catch {}
  }, [copyFuturesOpen]);


React.useEffect(() => {
  try { localStorage.setItem("tp:favoritesOpen", favoritesOpen ? "1" : "0"); } catch {}
}, [favoritesOpen]);


  const { setTheme } = useTheme();
  
  const isFuturesMode = settings["type"] === "Futures";
  const isForexMode = settings["type"] === "Forex";
  const isStockMode = settings["type"] === "Stocks" || settings["type"] === "Stock";
  const isOptionsMode = settings["type"] === "Options";
  const isMixedMode = settings["type"] === "Mixed";
  const isMannyGoogleSheet = platform === "mannysgooglesheet";
  const acceptTypes = platform === "mt5" ? ".csv,.xlsx" : ".csv";

  const filteredPlatforms = isForexMode
      ? PLATFORMS.filter(p =>
          p.value === 'mannysgooglesheet' ||
          p.value === 'trapound' ||
          p.value === 'mt5'
        )
      : PLATFORMS.filter(p => p.value !== 'mannysgooglesheet' && p.value !== 'mt5');

  const [fundAmount, setFundAmount] = useState("");
  const [funding, setFunding] = useState(false);
  const [fundMsg, setFundMsg] = useState("");
  const [fundErr, setFundErr] = useState("");
  const [peerAccounts, setPeerAccounts] = useState([]);

  // Robust prop/retail detection (supports either key)
  const isProp = (settings?.isProp ?? settings?.isPropFirm) === true;
  // Prop Funded flag set on the account metadata at creation
  const isPropFunded = settings?.isPropFunded === true;

   // Backtesting detector (works with either field you’ve saved)
   const isBacktesting =
     settings?.isBacktest === true ||
     String(settings?.tag || "").toLowerCase().includes("backtest") ||
     String(settings?.fundingType || "").toLowerCase().includes("backtest");
  // Show funding for Retail OR Prop Funded (but not for Prop Evaluation)
  const showFundingSection = (!isProp || isPropFunded) && !isBacktesting;

  const accountCurrency = settings["Account Currency"] || "USD";
  const runningBalance = Number(settings["Running Balance"] || 0);
  const accountGoal = Number(settings["Account Goal"] || 0);
  // Snapshots used by preview/info rows
  const startRB = runningBalance;

  const startPRB =
    typeof settings["Prop Running Balance"] !== "undefined"
      ? Number(settings["Prop Running Balance"] || 0)
      : Math.max(
          0,
          Number(settings["Initial Balance"] || 0) -
          Number(settings["Maximum Loss Limit"] || 0)
        );

const [phoneNumber, setPhoneNumber] = useState("");
const [smsModalOpen, setSmsModalOpen] = useState(false);
const [pendingPhoneNumber, setPendingPhoneNumber] = useState("");
const [pendingSmsConsent, setPendingSmsConsent] = useState(false);
const [savingSmsConsent, setSavingSmsConsent] = useState(false);
const [smsModalError, setSmsModalError] = useState("");

const isValidSmsPhone = /^\+[1-9]\d{7,14}$/.test(
  pendingPhoneNumber.trim()
);

  // Fetch the phone number directly from the root user document
  useEffect(() => {
    if (!userId) return;
    getDoc(doc(db, "users", userId)).then(snap => {
      if (snap.exists()) {
        setPhoneNumber(snap.data().phoneNumber || "");
      }
    });
  }, [userId]);

const openSmsConsentModal = () => {
  const consentMatchesCurrentNumber =
    settings["SMS Consent"] === true &&
    settings["SMS Consent Phone Number"] === phoneNumber;

  setPendingPhoneNumber(phoneNumber);
  setPendingSmsConsent(consentMatchesCurrentNumber);
  setSmsModalError("");
  setSmsModalOpen(true);
};

const closeSmsConsentModal = () => {
  if (savingSmsConsent) return;

  setSmsModalOpen(false);
  setPendingSmsConsent(false);
  setSmsModalError("");
};

const handleReminderMethodSelection = (method) => {
  if (method === "Text") {
    openSmsConsentModal();
    return;
  }

  handleSettingChange("Reminder Method", method);
};

const handleConfirmSmsConsent = async () => {
  if (!isValidSmsPhone || !pendingSmsConsent) return;

  const now = new Date().toISOString();
  const trimmedPhoneNumber = pendingPhoneNumber.trim();

  const consentPatch = {
    "Reminder Method": "Text",
    "SMS Consent": true,
    "SMS Consent Timestamp": now,
    "SMS Consent Disclosure Version": "2026-07-14",
    "SMS Consent Phone Number": trimmedPhoneNumber,
    "SMS Opt Out Timestamp": ""
  };

  setSavingSmsConsent(true);
  setSmsModalError("");

  try {
    await updateDoc(doc(db, "users", userId), {
      phoneNumber: trimmedPhoneNumber
    });

    await updateUserPrefs(userId, consentPatch);

    const accountCleanup = {};

    Object.keys(consentPatch).forEach((patchKey) => {
      accountCleanup[`settings.${patchKey}`] =
        deleteField();

      accountCleanup[patchKey] =
        deleteField();
    });

    await updateAccountSettings(
      userId,
      accountId,
      accountCleanup
    );

    setPhoneNumber(trimmedPhoneNumber);

    setSettings((prev) => ({
      ...prev,
      ...consentPatch
    }));

    setSmsModalOpen(false);
    setPendingSmsConsent(false);
  } catch (error) {
    console.error("Error enabling SMS reminders:", error);

    setSmsModalError(
      "We couldn't enable text reminders. Please try again."
    );
  } finally {
    setSavingSmsConsent(false);
  }
};

  // Live previews
  const amtNum = Number(fundAmount || 0);
  const previewAdd      = isFinite(amtNum) && amtNum > 0 ? round2(runningBalance + amtNum) : runningBalance;
  const previewWithdraw = isFinite(amtNum) && amtNum > 0 ? Math.max(0, round2(runningBalance - amtNum)) : runningBalance;

const [subStatus, setSubStatus] = useState("");

useEffect(() => {


  const subscribersRef = collection(db, "subscribers");
  
  // Create a query that checks for the ID in both potential fields
  // If your Firebase version doesn't support 'or', we check for userId first
  const q = query(subscribersRef, where("userId", "==", userId));

  const unsubscribe = onSnapshot(q, (snapshot) => {
    if (!snapshot.empty) {
      const subData = snapshot.docs[0].data();

      setSubStatus(subData.status || ""); 
    } else {
      // Fallback: Check if it's stored under 'ownerId' instead

      const q2 = query(subscribersRef, where("ownerId", "==", userId));
      
      getDocs(q2).then((ownerSnap) => {
        if (!ownerSnap.empty) {
          const ownerData = ownerSnap.docs[0].data();

          setSubStatus(ownerData.status || "");
        } else {

          setSubStatus("");
        }
      });
    }
  }, (error) => {
    console.error("DEBUG: Snapshot Error:", error);
  });

  return () => unsubscribe();
}, [userId]);

// This will now correctly evaluate to true
const isTrialing = String(subStatus).toUpperCase() === "TRIALING";



  // Keys that should live on the *account* doc, not the global userSettings doc
const ACCOUNT_SCOPED_KEYS = new Set([
  "Maximum Contracts","Minimum Contracts","Max Lot Size","Min Lot Size", "Maximum Shares","Minimum Shares",
  "Account Currency","Leverage","Daily Goal","Daily Loss Limit",
  "Risk Percentage","Drawdown Limit %","Max Trades Per Day",
  "Max Loss Trades Per Day","Drawdown Limit % Toggle","Daily Tick Goal",
  "Max Trades Per Day Toggle","Max Loss Trades Per Day Toggle",
  "Calculation by Entry Price", "Default Timeframe",
]);

async function importMT5(file, journalRows) {
  try {
    let rowsAA = [];
    const isXlsx = /\.xlsx?$/i.test(file.name);

    // Read file (XLSX via SheetJS; CSV via Papa)
    if (isXlsx) {
      // If you haven't already: npm i xlsx
      let XLSX;
      try {
        // dynamic import keeps bundle small
        XLSX = (await import("xlsx")).default || (await import("xlsx"));
      } catch (e) {
        setImportLog(l => [...l, "MT5: XLSX reader not available. Install the 'xlsx' package or export your MT5 report as CSV."]);
        setImporting(false);
        return;
      }
      const buf = await file.arrayBuffer();
      const wb  = XLSX.read(buf);
      const ws  = wb.Sheets[wb.SheetNames[0]];
      rowsAA    = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, blankRows: false, defval: null });
    } else {
      const text = await file.text();
      const parsed = Papa.parse(text, { header: false, skipEmptyLines: "greedy" });
      rowsAA = parsed.data;
    }

    // Find the "Positions" header row: 
    // Time | Position | Symbol | Type | Volume | Price | S / L | T / P | Time | Price | Commission | Swap | Profit
    const hdrIdx = rowsAA.findIndex(r => {
      const a = String(r?.[0] ?? "").trim().toLowerCase();  // "time"
      const c = String(r?.[2] ?? "").trim().toLowerCase();  // "symbol"
      const d = String(r?.[3] ?? "").trim().toLowerCase();  // "type"
      const e = String(r?.[4] ?? "").trim().toLowerCase();  // "volume"
      return a === "time" && c === "symbol" && d === "type" && e.includes("volume");
    });
    if (hdrIdx === -1) {
      setImportLog(l => [...l, "MT5: Could not find the Positions table. Export the MT5 Trade History report (Positions)."]);
      setImporting(false);
      return;
    }

    // Existing fingerprints for de‑dup
    const existingFingerprints = new Set(
      (journalRows || []).map(r => r.importFingerprint).filter(Boolean)
    );

    // Account context & balances
    const accountData = await getAccountSettings(userId, accountId);
    const s = accountData?.settings || {};
    const isPropAcct = Boolean(s.isProp || s.isPropFirm || s.isPropFunded);

    const startRB  = Number(s["Running Balance"] || 0);
    const startPRB = Number(
      s["Prop Running Balance"] ??
      Math.max(0, Number(s["Initial Balance"] || 0) - Number(s["Maximum Loss Limit"] || 0))
    );

    let runningBalance      = startRB;
    let propRunningBalance  = startPRB;

    // Helpers
    const toNum = v => parseSheetNumber(v);
    const parseDT = v => {
      if (v instanceof Date) return v;
      const t = String(v || "").trim();
      if (!t) return null;
      const iso = t.replace(/\./g, "-").replace(" ", "T");
      const d = new Date(iso);
      return isNaN(d.getTime()) ? null : d;
    };
    const hhmm = d => `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    const pipSizeFor = sym => {
      const u = String(sym || "").toUpperCase();
      if (u.endsWith("JPY")) return 0.01;  // JPY pairs
      if (u.startsWith("XAU")) return 0.1; // gold (in case it appears)
      if (u.startsWith("XAG")) return 0.01;
      return 0.0001;                       // most FX pairs
    };

    let imported = 0, skipped = 0, dupeArr = [];

    for (let i = hdrIdx + 1; i < rowsAA.length; i++) {
      const r = rowsAA[i];
      if (!r) continue;

      const firstCell = r[0];
      // stop at next section (e.g., "Orders") or blanks
      if (firstCell == null || String(firstCell).trim().toLowerCase().startsWith("orders")) break;
      // data rows start with a time like "2025.10.23 02:38:05"
      if (!String(firstCell).match(/^\d{4}[.\-]\d{2}[.\-]\d{2}/)) continue;

      // ---- Columns per MT5 Positions table
      const entryDT     = parseDT(r[0]);                     // Time (open)
      const positionId  = r[1];                              // Position #
      const symbol      = String(r[2] || "").trim();         // Symbol -> Product
      const sideRaw     = String(r[3] || "").trim().toLowerCase(); // Type -> Position
      const volume      = toNum(r[4]);                       // Volume -> Size
      const entryPrice  = toNum(r[5]);                       // Price (entry)
      const slRaw       = r[6];                              // S/L
      const tpRaw       = r[7];                              // T/P
      const exitDT      = parseDT(r[8]);                     // Time (close)
      const exitPrice   = toNum(r[9]);                       // Price (close)
      const commission  = toNum(r[10]);                      // Commission
      const swap        = toNum(r[11]);                      // Swap
      const profit      = toNum(r[12]);                      // Profit

      if (!entryDT || !exitDT || !symbol) continue;

      // De‑dup fingerprint (stable across re-imports)
      const fp = `${formatDate(entryDT)}|${symbol}|${sideRaw}|${volume}|${entryPrice}|${exitPrice}`.toLowerCase();
      if (existingFingerprints.has(fp)) { skipped++; dupeArr.push(String(positionId || fp)); continue; }

      // Pips (directional)
      const pipSz      = pipSizeFor(symbol);
      const signedPips = sideRaw === "buy"
        ? (exitPrice - entryPrice) / pipSz
        : (entryPrice - exitPrice) / pipSz;
      const pips = Math.round(signedPips * 10) / 10;

      // SL/TP (pips), if available
      let slPips = "";
      const sl = toNum(slRaw);
      if (Number.isFinite(sl) && sl > 0) {
        const v = sideRaw === "buy" ? (entryPrice - sl) / pipSz : (sl - entryPrice) / pipSz;
        slPips = Math.abs(Math.round(v * 10) / 10);
      }
      let tpPips = "";
      const tp = toNum(tpRaw);
      if (Number.isFinite(tp) && tp > 0) {
        const v = sideRaw === "buy" ? (tp - entryPrice) / pipSz : (entryPrice - tp) / pipSz;
        tpPips = Math.abs(Math.round(v * 10) / 10);
      }

      // Actual Reward = Profit − (Commission + Swap)
      const actualReward = +(profit - (commission + swap)).toFixed(2);

      // Balances & percentages
      const beforeBal   = runningBalance; 
      runningBalance    = +(runningBalance + actualReward).toFixed(2);
      propRunningBalance = +(propRunningBalance + actualReward).toFixed(2);
      const growthPct   = beforeBal > 0 ? ((actualReward / beforeBal) * 100) : 0;

      const tradeObj = {
        "Trade #": Number(positionId) || Date.now() + imported,

        // Dates & times
        "Date": formatDate(entryDT),                             // Time (first Time, date part) -> Date
        "Entry Time": toAmPm(hhmm(entryDT)),                     // Time (first Time) -> Entry Time
        "Exit Time":  toAmPm(hhmm(exitDT)),                      // Exit Time (second Time)
        "Duration":   calculateDuration(entryDT, exitDT),        // Exit - Entry -> Duration
        "Duration (h:mm)": calculateDuration(entryDT, exitDT),

        // Product & side
        "Product":  symbol,                                      // Symbol -> Product
        "Position": sideRaw === "sell" ? "Sell" : "Buy",         // Type -> Position
        "Size":     Number(isFinite(volume) ? volume : 0),       // Volume -> Size

        // Prices
        "Entry Price": Number(entryPrice || 0),                  // Entry Price -> Entry Price
        "Exit Price":  Number(exitPrice || 0),                   // Exit Price -> Exit Price

        // Pips / SL / TP
        "Pips": pips,
        "Stop Loss_Take Profit":
          slPips !== "" && tpPips !== ""
            ? `${slPips}/${tpPips}`
            : slPips !== ""
            ? `${slPips}`
            : tpPips !== ""
            ? `${tpPips}`
            : "",

        // PnL, balances & flags
        "Actual Reward": actualReward,                           // Profit - (Comm + Swap)
        "Balance Before Trade": beforeBal,
        "Actual Balance": runningBalance,
        "Actual Percent Growth": `${growthPct.toFixed(2)}%`,     // Percent %
        "Percent of Balance": Number(growthPct.toFixed(2)),

        // Win/Loss from Profit
        "Win":  profit > 0,
        "Loss": profit < 0,

        "Notes": `Imported from MT5${positionId ? " #" + positionId : ""}`,
        "created": new Date(),
        "importFingerprint": fp
      };

      await appendTrade(userId, accountId, tradeObj);
      existingFingerprints.add(fp);
      imported++;
    }

    // Persist balances
    const delta = runningBalance - startRB;
    const updates = { "settings.Running Balance": runningBalance };
    if (isPropAcct) updates["settings.Prop Running Balance"] = +(startPRB + delta).toFixed(2);
    await updateAccountSettings(userId, accountId, updates);

    setImportLog(l => [`MT5 import complete. Imported: ${imported}, Skipped Duplicates: ${skipped}`, ...l]);
    if (dupeArr.length) setImportDuplicates(dupeArr);
  } catch (err) {
    console.error("[MT5 import] failed:", err);
    setImportLog(l => [...l, `MT5 import failed: ${err?.message || err}`]);
  } finally {
    setImporting(false);
  }
}


useEffect(() => {
  if (!userId) return;

  const ref = doc(db, "users", userId, "settings", "userSettings");
  const unsub = onSnapshot(
    ref,
    (snap) => {
      const favs = snap.data()?.favorites || { forex: [], futures: [] };
      // keep local state in sync with the document
      setFaveForex(new Set(favs.forex || []));
      setFaveFutures(new Set(favs.futures || []));
    },
    (err) => console.error("[Settings] favorites snapshot error", err)
  );

  return unsub;
}, [userId]);


useEffect(() => {
  if (!userId) return;
  (async () => {
    try { setAccountsList(await listUserAccounts(userId)); } catch {}
  })();
}, [userId]);

useEffect(() => {
  const enabled = futuresAccounts.some(
    a => a?.copyTrade?.group === "futures" && (a?.copyTrade?.role === "leader" || a?.copyTrade?.role === "follower")
  );
  setCopyEnabledFutures(enabled);
}, [futuresAccounts]);

useEffect(() => {
  if (!userId) return;
  (async () => {
    try {
      const prefs = await getUserSettings(userId);
      setFaveForex(new Set(prefs?.favorites?.forex || []));
      setFaveFutures(new Set(prefs?.favorites?.futures || []));
    } catch (e) {
      console.error("[Settings] load favorites failed", e);
    }
  })();
}, [userId]);


useEffect(() => {
  // FUTURES: if there is a futures leader, use their flag; else default ON
  const leaderFut = (futuresAccounts || []).find(a => a?.settings?.CopyTrade?.role === "leader");
  if (leaderFut) setCopyEnabledFutures(leaderFut.settings.CopyTrade.enabled !== false);

  // FOREX: same idea (if you have a forex list; see handler below)
  const fx = (allAccounts || []).filter(a => String(a.type).toLowerCase() === "forex");
  const leaderFx = fx.find(a => a?.settings?.CopyTrade?.role === "leader");
  if (leaderFx) setCopyEnabledForex(leaderFx.settings.CopyTrade.enabled !== false);
}, [futuresAccounts, allAccounts]);

useEffect(() => {
  if (!userId) return;
  (async () => {
    const snap = await getDocs(collection(db, "users", userId, "accounts"));
    const all = snap.docs.map(d => {
      const data = d.data() || {};
      const name = data.name || data.accountName || d.id;
      const type = (data.settings?.type) || data.type || "";
      return { id: d.id, name, type, settings: data.settings || {} };
    });
    setPeerAccounts(all);
  })();
}, [userId]);

useEffect(() => {
  if (!userId) return;
  (async () => {
    const snap = await getDocs(collection(db, "users", userId, "accounts"));
    const list = snap.docs.map(d => {
      const data = d.data() || {};
      const nested = data.settings || {};
      return {
        id: d.id,
        name: data.name || nested.name || data["Account Name"] || d.id,
        type: data.type || nested.type || "",
        copyTrade: nested.CopyTrade || data.CopyTrade || null
      };
    });

    setAllAccounts(list);

    // --- FUTURES (existing) ---
    const fut = list.filter(a => String(a.type).toLowerCase() === "futures");
    setFuturesAccounts(fut);
    const futLeader = fut.find(a => a.copyTrade?.role === "leader");
    if (futLeader) {
      setLeaderId(futLeader.id);
      const futFollowers = fut
        .filter(a => a.copyTrade?.role === "follower" && a.copyTrade?.leaderId === futLeader.id)
        .map(a => a.id);
      setFollowerIds(new Set(futFollowers));
      setCopyEnabledFutures(Boolean(futLeader.copyTrade?.enabled ?? true));
    } else {
      setLeaderId("");
      setFollowerIds(new Set());
      setCopyEnabledFutures(true);
    }

    // --- FOREX (new) ---
    const fx = list.filter(a => String(a.type).toLowerCase() === "forex");
    setForexAccounts(fx);
    const fxLeader = fx.find(a => a.copyTrade?.role === "leader");
    if (fxLeader) {
      setFxLeaderId(fxLeader.id);
      const fxFollowers = fx
        .filter(a => a.copyTrade?.role === "follower" && a.copyTrade?.leaderId === fxLeader.id)
        .map(a => a.id);
      setFxFollowerIds(new Set(fxFollowers));
      setCopyEnabledForex(Boolean(fxLeader.copyTrade?.enabled ?? true));
    } else {
      setFxLeaderId("");
      setFxFollowerIds(new Set());
      // NEW: honor explicit OFF even when there’s no leader
      const anyExplicitOff = fx.some(a => a.copyTrade && a.copyTrade.enabled === false);
      setCopyEnabledForex(!anyExplicitOff);
    }
  })();
}, [userId]);

useEffect(() => {
  if (!userId) return;
  (async () => {
    try {
      const prefs = await getUserSettings(userId);
      setFaveForex(new Set(prefs?.favorites?.forex || []));
      setFaveFutures(new Set(prefs?.favorites?.futures || []));
    } catch (e) {
      console.error("[Settings] load favorites failed", e);
    }
  })();
}, [userId]);

useEffect(() => {
  if (!userId) return;
  (async () => {
    const snap = await getDocs(collection(db, "users", userId, "accounts"));
    const list = snap.docs.map(d => {
      const data = d.data() || {};
      const nested = data.settings || {};
      return {
        id: d.id,
        name: data.name || nested.name || data["Account Name"] || d.id,
        type: data.type || nested.type || "",
        copyTrade: nested.CopyTrade || data.CopyTrade || null
      };
    });

    // --- FOREX ---
    const fx = list.filter(a => String(a.type).toLowerCase() === "forex");
    setForexAccounts(fx);
    const fxLeader = fx.find(a => a.copyTrade?.role === "leader");
    if (fxLeader) {
      setFxLeaderId(fxLeader.id);
      const fxFollowers = fx
        .filter(a => a.copyTrade?.role === "follower" && a.copyTrade?.leaderId === fxLeader.id)
        .map(a => a.id);
      setFxFollowerIds(new Set(fxFollowers));
      setCopyEnabledForex(Boolean(fxLeader.copyTrade?.enabled ?? true));
    } else {
      setFxLeaderId("");
      setFxFollowerIds(new Set());
      setCopyEnabledForex(true);
    }
  })();
}, [userId]);


useEffect(() => {
  if (!userId || !accountId) return;
  setLoading(true);

const fetchAllSettings = async () => {
  try {
    const userSettings = await getUserSettings(userId);
    const userPrefs = await getUserPrefs(userId); // Fetch the prefs document
    const accountData  = await getAccountSettings(userId, accountId);

      // --- One-time migration: strip account-scoped keys from GLOBAL userSettings
      if (userSettings && Object.keys(userSettings).some(k => ACCOUNT_SCOPED_KEYS.has(k))) {
        const cleanup = {};
        for (const k of Object.keys(userSettings)) {
          if (ACCOUNT_SCOPED_KEYS.has(k)) cleanup[k] = deleteField();
        }
        if (Object.keys(cleanup).length) {
          await updateUserSettings(userId, cleanup);
          for (const k of Object.keys(cleanup)) delete userSettings[k];
        }
      }

      // Pull account fields
      const nestedSettingsRaw = accountData?.settings || {};
      const { settings: _ignored, ...topLevelFieldsRaw } = accountData || {};

      // Drop account-scoped keys from top-level so nested wins for those
      const topLevelFields = { ...topLevelFieldsRaw };
      for (const k of Object.keys(topLevelFields)) {
        if (ACCOUNT_SCOPED_KEYS.has(k)) delete topLevelFields[k];
      }

      // --- NEW: never allow user-level keys on the account doc
      // 👇 ADDED "Privacy Mode" TO THIS LIST
      const PREFS_KEYS = new Set([
        "Theme",
        "Pretrade Checklist",
        "PretradeChecklistShownDate",
        "hasSeenWelcomeVideo",
        "hasSeenWhatsNew_v4",
        "pretradeChecklistLastShown",
        "Privacy Mode"
      ]);

      const USER_LEVEL_KEYS = new Set([
        "Privacy Mode",
        "EOD Reminders",
        "Reminder Method",
        "SMS Consent",
        "SMS Consent Timestamp",
        "SMS Consent Disclosure Version",
        "SMS Consent Phone Number",
        "SMS Opt Out Timestamp"
      ]);
      
const accCleanup = {};
    // Clean up both global and pref keys from the account document
    for (const k of [...USER_LEVEL_KEYS, ...PREFS_KEYS]) {
      if (k in topLevelFieldsRaw) accCleanup[k] = deleteField();                  
      if (k in nestedSettingsRaw) accCleanup[`settings.${k}`] = deleteField();    
    }
    if (Object.keys(accCleanup).length) {
      await updateAccountSettings(userId, accountId, accCleanup);
    }

    // Sanitize account fields in-memory
    const sanitizedTopLevel = Object.fromEntries(
      Object.entries(topLevelFields).filter(([k]) => !USER_LEVEL_KEYS.has(k) && !PREFS_KEYS.has(k))
    );
    const sanitizedNested = Object.fromEntries(
      Object.entries(nestedSettingsRaw).filter(([k]) => !USER_LEVEL_KEYS.has(k) && !PREFS_KEYS.has(k))
    );

    // Grab true user-level settings
    const filteredUserSettings = Object.fromEntries(
      Object.entries(userSettings || {}).filter(([k]) => USER_LEVEL_KEYS.has(k))
    );

    // Grab the UI preferences
    const filteredPrefsData = Object.fromEntries(
      Object.entries(userPrefs || {}).filter(([k]) => PREFS_KEYS.has(k))
    );

    // Final merge: account keys + user settings + UI prefs
    const mergedSettings = {
      ...defaultSettings,
      ...sanitizedTopLevel,
      ...sanitizedNested,
      ...filteredUserSettings,
      ...filteredPrefsData,
    };

    setSettings(mergedSettings);
    
      // ✅ Compute backtesting from the account doc you just loaded (NOT from React state)
      const isBacktestingAcc =
        accountData?.isBacktest === true ||
        nestedSettingsRaw?.isBacktest === true ||
        String(accountData?.tag || "").toLowerCase().includes("backtest") ||
        String(nestedSettingsRaw?.tag || "").toLowerCase().includes("backtest") ||
        String(accountData?.fundingType || "").toLowerCase().includes("backtest") ||
        String(nestedSettingsRaw?.fundingType || "").toLowerCase().includes("backtest");

      // ✅ Auto-hide Mood only if user has never set it before
      if (isBacktestingAcc && typeof mergedSettings["Hide Mood"] === "undefined") {
        // Optimistically update UI so it doesn't flash visible
        setSettings(prev => ({ ...prev, "Hide Mood": true }));

        // Persist to account.settings
        await updateAccountSettings(userId, accountId, {
          "settings.Hide Mood": true
        });
      }

    } catch (err) {
      console.error("Error fetching settings:", err);
    } finally {
      setLoading(false);
    }
  };

  fetchAllSettings();
}, [userId, accountId]);






const handlePickLeader = (id) => {
  setLeaderId(id);
  // A leader cannot also be a follower—remove it from followers if present
  setFollowerIds(prev => {
    const next = new Set(prev);
    next.delete(id);
    return next;
  });
};

const handleToggleFollower = (id) => {
  if (id === leaderId) return; // cannot follow yourself
  setFollowerIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
};

const handlePickFxLeader = (id) => {
  setFxLeaderId(id);
  setFxFollowerIds(prev => {
    const next = new Set(prev);
    next.delete(id); // leader cannot follow itself
    return next;
  });
};



const handleToggleFxFollower = (id) => {
  if (id === fxLeaderId) return; // cannot follow yourself
  setFxFollowerIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
};

async function persistFuturesCopyEnabled(val) {
  try {
    setCopyEnabledFutures(val);
    const batch = writeBatch(db);
    for (const acc of (futuresAccounts || [])) {
      batch.update(doc(db, "users", userId, "accounts", acc.id), {
        "settings.CopyTrade.enabled": val
      });
    }
    await batch.commit();
    setCopyTradeMsg(val ? "Futures copy trading is ON, Click Save to Confirm." : "Futures copy trading is OFF, Click Save to Confirm.");
  } catch (e) {
    console.error("Failed to toggle Futures copy trading", e);
  }
}

// Toggle ON/OFF for ALL Forex accounts (persists the flag)
async function persistForexCopyEnabled(val) {
  try {
    setCopyEnabledForex(val);
    const fx = (allAccounts || []).filter(a => String(a.type).toLowerCase() === "forex");
    const batch = writeBatch(db);
    for (const acc of fx) {
      batch.update(doc(db, "users", userId, "accounts", acc.id), {
        "settings.CopyTrade.enabled": val
      });
    }
    await batch.commit();
    setCopyTradeMsg(val ? "Forex copy trading is ON, Click Save to Confirm." : "Forex copy trading is OFF, Click Save to Confirm.");
  } catch (e) {
    console.error("Failed to toggle Forex copy trading", e);
  }
}



// Save (clear old roles, apply new roles)
const handleSaveCopyTradeForex = async () => {
  setSavingCopyTrade(true);
  setCopyTradeMsg("");

  try {
    const batch = writeBatch(db);
    const group = forexAccounts;

    const selectedFollowers = fxLeaderId ? Array.from(fxFollowerIds).filter(id => id !== fxLeaderId) : [];

    for (const acc of group) {
      const ref = doc(db, "users", userId, "accounts", acc.id);
      let updates = {};

      if (!copyEnabledForex) {
        updates = {
          "settings.CopyTrade.role": deleteField(),
          "settings.CopyTrade.group": deleteField(),
          "settings.CopyTrade.followerIds": deleteField(),
          "settings.CopyTrade.leaderId": deleteField(),
          "settings.CopyTrade.enabled": false,
          "settings.roleTag": deleteField(),
          "CopyTrade": deleteField()
        };
      } else {
        if (acc.id === fxLeaderId) {
          updates = {
            "settings.CopyTrade": {
              role: "leader",
              group: "forex",
              enabled: true,
              followerIds: selectedFollowers
            },
            "settings.roleTag": "Leader",
            "CopyTrade": deleteField()
          };
        } else if (selectedFollowers.includes(acc.id)) {
          updates = {
            "settings.CopyTrade": {
              role: "follower",
              group: "forex",
              enabled: true,
              leaderId: fxLeaderId
            },
            "settings.roleTag": "Follower",
            "CopyTrade": deleteField()
          };
        } else {
          updates = {
            "settings.CopyTrade": deleteField(),
            "settings.roleTag": deleteField(),
            "CopyTrade": deleteField()
          };
        }
      }

      // ✅ Execute exactly ONE update per document
      batch.update(ref, updates);
    }

    await batch.commit();
    setCopyTradeMsg("Copy trade roles saved.");

    if (!copyEnabledForex) {
      setFxLeaderId("");
      setFxFollowerIds(new Set());
    }

  } catch (e) {
    console.error("[Settings] save copy-trade (forex) failed", e);
    setCopyTradeMsg(e?.message ? `Save failed: ${e.message}` : "Save failed.");
  } finally {
    setSavingCopyTrade(false);
  }
};



const toggleForex = (sym) =>
  setFaveForex(prev => {
    const next = new Set(prev);
    next.has(sym) ? next.delete(sym) : next.add(sym);
    return next;
  });

const toggleFutures = (value) => {
  const code = futuresCodeOf(value); // e.g., "YM" from "YM - Dow - $5.00"
  setFaveFutures(prev => {
    const next = new Set(prev);
    next.has(code) ? next.delete(code) : next.add(code);
    return next;
  });
};

 const selectAllForex = () =>
   setFaveForex(new Set([
     ...FOREX_PAIRS.map(([v]) => v),
     ...FOREX_INDICES.map(([v]) => v),
   ]));
const clearAllForex  = () => setFaveForex(new Set());
const selectAllFut   = () => setFaveFutures(new Set(FUTURES_PRODUCTS.map(([v]) => futuresCodeOf(v))));
const clearAllFut    = () => setFaveFutures(new Set());

async function handleSaveFavorites() {
  setSavingFavorites(true);
  setFavoritesMsg("");
  try {
    await updateUserSettings(userId, {
      favorites: {
        forex:   Array.from(faveForex),
        futures: Array.from(faveFutures),
      }
    });
    setFavoritesMsg("Favorites saved.");
  } catch (e) {
    console.error("[Settings] save favorites failed", e);
    setFavoritesMsg("Save failed.");
  } finally {
    setSavingFavorites(false);
  }
}


// Turn Forex copy-trading ON/OFF for all Forex accounts
async function setForexCopyEnabled(val) {
  try {
    setCopyEnabledForex(val);
    const fx = (allAccounts || []).filter(a => String(a.type).toLowerCase() === "forex");
    const batch = writeBatch(db);
    for (const acc of fx) {
      batch.update(doc(db, "users", userId, "accounts", acc.id), {
        "settings.CopyTrade.enabled": val
      });
    }
    await batch.commit();
    setCopyTradeMsg(val ? "Forex copy trading settings saved." : "Forex copy trading settings saved.");
  } catch (e) {
    console.error("Failed to toggle Forex copy trading", e);
  }
}
const handleSaveCopyTradeFutures = async () => {
  setSavingCopyTrade(true);
  setCopyTradeMsg("");

  try {
    const batch = writeBatch(db);
    const group = futuresAccounts;

    // Filter out the leader from followers safely
    const selectedFollowers = leaderId ? Array.from(followerIds).filter(id => id !== leaderId) : [];

    for (const acc of group) {
      const ref = doc(db, "users", userId, "accounts", acc.id);
      let updates = {};

      if (!copyEnabledFutures) {
        // Completely clear roles if disabled
        updates = {
          "settings.CopyTrade.role": deleteField(),
          "settings.CopyTrade.group": deleteField(),
          "settings.CopyTrade.followerIds": deleteField(),
          "settings.CopyTrade.leaderId": deleteField(),
          "settings.CopyTrade.enabled": false,
          "settings.roleTag": deleteField(),
          "CopyTrade": deleteField()
        };
      } else {
        // Apply roles correctly in a SINGLE update block
        if (acc.id === leaderId) {
          updates = {
            "settings.CopyTrade": {
              enabled: true,
              role: "leader",
              group: "futures",
              followerIds: selectedFollowers
            },
            "settings.roleTag": "Leader",
            "CopyTrade": deleteField() // Cleanup legacy key
          };
        } else if (selectedFollowers.includes(acc.id)) {
          updates = {
            "settings.CopyTrade": {
              enabled: true,
              role: "follower",
              group: "futures",
              leaderId: leaderId
            },
            "settings.roleTag": "Follower",
            "CopyTrade": deleteField() // Cleanup legacy key
          };
        } else {
          // If it's in the group but not selected as leader/follower, clear previous roles
          updates = {
            "settings.CopyTrade": deleteField(),
            "settings.roleTag": deleteField(),
            "CopyTrade": deleteField()
          };
        }
      }

      // ✅ Execute exactly ONE update per document
      batch.update(ref, updates);
    }

    await batch.commit();
    setCopyTradeMsg("Futures copy trade settings saved.");
    
    // Clear UI state if disabled
    if (!copyEnabledFutures) {
      setLeaderId("");
      setFollowerIds(new Set());
    }

  } catch (e) {
    console.error("[Settings] save copy-trade failed", e);
    setCopyTradeMsg(e?.message ? `Save failed: ${e.message}` : "Save failed.");
  } finally {
    setSavingCopyTrade(false);
  }
};




async function saveCopyTrading() {
  // Make a clean snapshot of the choices currently in UI
  const role = settings["Copy Role"] || "None";
  const followers = Array.isArray(settings["Copy Followers"]) ? settings["Copy Followers"] : [];
  const leaderId = settings["Copy Leader Id"] || "";

  // Load previous values so we can diff
  const prev = await getAccountSettings(userId, accountId); // you already have this helper in this file
  const prevRole = prev?.settings?.["Copy Role"] || "None";
  const prevFollowers = prev?.settings?.["Copy Followers"] || [];
  const prevLeaderId = prev?.settings?.["Copy Leader Id"] || "";

  const batch = writeBatch(db);

  // Persist my account's settings
  batch.update(doc(db, "users", userId, "accounts", accountId), {
    "settings.Copy Role": role,
    "settings.Copy Followers": role === "Leader" ? followers : [],
    "settings.Copy Leader Id": role === "Follower" ? leaderId : "",
  });

  // If I'm a LEADER, add/remove followers (same-type filtering was handled in UI)
  if (role === "Leader") {
    const toAdd = followers.filter(id => !prevFollowers.includes(id));
    const toRemove = prevFollowers.filter(id => !followers.includes(id));

    // mark new followers as Followers pointing to me
    toAdd.forEach(fid => {
      batch.update(doc(db, "users", userId, "accounts", fid), {
        "settings.Copy Role": "Follower",
        "settings.Copy Leader Id": accountId,
      });
    });

    // clear unselected followers
    toRemove.forEach(fid => {
      batch.update(doc(db, "users", userId, "accounts", fid), {
        "settings.Copy Role": "None",
        "settings.Copy Leader Id": "",
      });
    });
  }

  // If I'm a FOLLOWER, ensure the selected leader contains me in their followers;
  // and remove me from any previous leader
  if (role === "Follower") {
    if (prevLeaderId && prevLeaderId !== leaderId) {
      const oldLeaderDoc = await getAccountSettings(userId, prevLeaderId);
      const oldFollowers = oldLeaderDoc?.settings?.["Copy Followers"] || [];
      const newOldFollowers = oldFollowers.filter(x => x !== accountId);
      batch.update(doc(db, "users", userId, "accounts", prevLeaderId), {
        "settings.Copy Followers": newOldFollowers,
      });
    }
    if (leaderId) {
      const newLeaderDoc = await getAccountSettings(userId, leaderId);
      const lf = newLeaderDoc?.settings?.["Copy Followers"] || [];
      if (!lf.includes(accountId)) {
        batch.update(doc(db, "users", userId, "accounts", leaderId), {
          "settings.Copy Followers": [...lf, accountId],
        });
      }
      // Make sure the leader is marked as Leader
      batch.update(doc(db, "users", userId, "accounts", leaderId), {
        "settings.Copy Role": "Leader",
      });
    }
  }

  await batch.commit();
}


async function copyTradeToFollowers({ delta, leaderTrade, group }) {
  try {
    // Leader’s nested settings (where CopyTrade config lives)
    const leaderSettings = await getSettings(userId, accountId);

    // Leader’s account doc (type/group lives here)
    const leaderAcc   = await getAccountSettings(userId, accountId); // local helper in this file
    const leaderGroup = String(leaderAcc?.type || "").toLowerCase();

    const ct = leaderSettings?.CopyTrade || {};

    // Only copy when configured properly
    if (ct.role !== "leader") return;
    if (ct.enabled === false) return;
    if (leaderGroup !== group) return;

    const followerIds = Array.isArray(ct.followerIds) ? ct.followerIds : [];
    if (!followerIds.length) return;

    for (const fid of followerIds) {
      if (!fid || fid === accountId) continue; // never copy back to self

      try {
        // Enforce group match on the follower by reading its account doc type
        const fAcc   = await getAccountSettings(userId, fid);
        const fGroup = String(fAcc?.type || "").toLowerCase();
        if (fGroup !== group) continue;

        // Follower balances come from follower's nested settings
        const fset    = await getSettings(userId, fid);
        const prevRB  = Number(fset?.["Running Balance"] || 0);
        const nextRB  = +(prevRB + Number(delta || 0)).toFixed(2);

        const hasPRB  = typeof fset?.["Prop Running Balance"] !== "undefined";
        const prevPRB = Number(fset?.["Prop Running Balance"] ?? prevRB);
        const nextPRB = +(prevPRB + Number(delta || 0)).toFixed(2);

        // Pick follower trade number
        let followerTradeNum;
        try { followerTradeNum = await getNextTradeNumber(userId, fid); }
        catch { followerTradeNum = Date.now(); }

        // Build the copied trade for the follower

         const leaderTradeNum = Number(leaderTrade?.["Trade #"] ?? leaderTrade?.tradeNum ?? 0);
         const copied = { ...(leaderTrade || {}) };

          copied["Trade #"]              = followerTradeNum;
          copied["tradeNum"]             = followerTradeNum;
         copied["Leader Trade #"]       = leaderTradeNum;
         copied["leaderTradeNum"]       = leaderTradeNum;
          copied["Balance Before Trade"] = prevRB;
          copied["Actual Reward"]        = +Number(delta || 0).toFixed(2);
          copied["Actual Balance"]       = nextRB;
          copied["Running Balance"]      = nextRB;
         copied["Copied From Account"]  = accountId; 
        if (prevRB > 0) {
          copied["Actual Percent Growth"] = (((Number(delta || 0)) / prevRB) * 100).toFixed(2) + "%";
        }

        // Optional provenance
        copied["Copied From Account"] = accountId;

        // Write the follower’s journal row (you already have this local helper)
        await appendTrade(userId, fid, copied);

        // Patch follower balances (nested settings merge)
        const patch = hasPRB
          ? { "Running Balance": nextRB, "Prop Running Balance": nextPRB }
          : { "Running Balance": nextRB };

        await updateSettings(userId, fid, patch);

      } catch (e) {
        console.error("[copyTrade] follower failed", fid, e);
      }
    }
  } catch (e) {
    console.error("[copyTrade] leader load failed", e);
  }
}

const { user } = useAuth();
  const [showDailySummary, setShowDailySummary] = useState(false);

  // Listen to the user's profile to get the current setting
  useEffect(() => {
    if (!user) return;
    const userRef = doc(db, "users", user.uid);
    const unsubscribe = onSnapshot(userRef, (docSnap) => {
      if (docSnap.exists()) {
        // Default to false if it doesn't exist
        setShowDailySummary(docSnap.data().showDailySummary === true);
      }
    });
    return () => unsubscribe();
  }, [user]);

  // Handler to toggle the setting in Firebase
  const handleToggleDailySummary = async () => {
    if (!user) return;
    const userRef = doc(db, "users", user.uid);
    try {
      // This will trigger the listener above and update the UI automatically
      await updateDoc(userRef, {
        showDailySummary: !showDailySummary
      });
    } catch (error) {
      console.error("Error updating settings:", error);
      alert("Failed to update setting.");
    }
  };

async function setCopyTradeRole(role) {
  const payload = {
    "settings.CopyTrade.role": role,
    "settings.CopyTrade.enabled": role !== "off",
    "settings.CopyTrade.accountType": acctType
  };
  if (role !== "follower") payload["settings.CopyTrade.leaderId"] = "";
  setSettings(prev => ({ ...prev, CopyTrade: { ...(prev.CopyTrade || {}), ...payload["settings.CopyTrade"] }}));
  await updateAccountSettings(userId, accountId, payload);
}

async function setCopyTradeLeader(leaderId) {
  const payload = {
    "settings.CopyTrade.role": "follower",
    "settings.CopyTrade.enabled": true,
    "settings.CopyTrade.accountType": acctType,
    "settings.CopyTrade.leaderId": leaderId
  };
  setSettings(prev => ({ ...prev, CopyTrade: { ...(prev.CopyTrade || {}), ...payload["settings.CopyTrade"] }}));
  await updateAccountSettings(userId, accountId, payload);
}


const themeSetting = settings["Theme"];

useEffect(() => {
  if (!loading && themeSetting) {
    setTheme(themeSetting.toLowerCase());
  }
}, [loading, themeSetting, setTheme]);

  useEffect(() => {
    setPlatform("");
  }, [settings["Trade Mode"]]);

  



  // Put this inside the Settings component (near your other handlers)
const handleSettingChange = (key, value) => {
  const now = new Date().toISOString();
  const patch = { [key]: value };

  if (
    (key === "EOD Reminders" && value !== true) ||
    (key === "Reminder Method" && value !== "Text")
  ) {
    patch["SMS Consent"] = false;
    patch["SMS Opt Out Timestamp"] = now;
  }

  setSettings((prev) => ({
    ...prev,
    ...patch
  }));

  // Reusable cleanup function to remove these keys from the account document
  const cleanupAccountFields = () => {
    const accountCleanup = {};
    Object.keys(patch).forEach((patchKey) => {
      accountCleanup[`settings.${patchKey}`] = deleteField();
      accountCleanup[patchKey] = deleteField();
    });
    updateAccountSettings(userId, accountId, accountCleanup);
  };

  // Route the patch to the correct document
  if (PREFS_KEYS.has(key)) {
    updateUserPrefsDoc(userId, patch);
    cleanupAccountFields();
  } else if (USER_LEVEL_KEYS.has(key)) {
    updateUserSettings(userId, patch);
    cleanupAccountFields();
  } else {
    // If it's neither, it belongs to the account document
    updateAccountSettings(userId, accountId, {
      [`settings.${key}`]: value
    });
  }
};

  // Find a cell by any header alias, ignoring case/spacing/underscores
function pickCell(row, ...aliases) {
  const norm = s => String(s).toLowerCase().replace(/[\s_]+/g, "");
  const byNorm = new Map(Object.keys(row).map(k => [norm(k), k]));
  for (const a of aliases) {
    const key = byNorm.get(norm(a));
    if (key && row[key] != null && String(row[key]).trim() !== "") {
      return String(row[key]).trim();
    }
  }
  return "";
}


  async function applyFunding(delta, action) {
    if ((settings?.isPropFunded === true) && action === "add") {
      setFundErr("Deposits are disabled on Prop Funded accounts.");
        return;
      }
    const amt = Number(fundAmount);
    if (!isFinite(amt) || amt <= 0) {
      setFundErr("Enter a positive amount.");
      return;
    }
    setFunding(true);
    setFundErr("");
    setFundMsg("");

    try {
      const current = Number(settings["Running Balance"] || 0);
      const goal = Number(settings["Account Goal"] || 0);

      const newBalance   = Math.max(0, round2(current + delta));
      const newRemaining = Math.max(0, round2(goal - newBalance));


      const updates = {
        "settings.Running Balance": newBalance,
        "settings.Beginning Balance": newBalance, // <-- FIX: Update the Total Equity Anchor
        "settings.Remaining Profit Amount": newRemaining,
      };

      // Clean up potential top-level duplicates (if any exist from other flows)
      updates["Running Balance"] = deleteField();
      updates["Beginning Balance"] = deleteField(); // <-- FIX: Cleanup
      updates["Remaining Profit Amount"] = deleteField();

      await updateAccountSettings(userId, accountId, updates);

      // Log funding to the Journal (visible but stats-safe)
      try {
        const now = new Date();
        const currency = settings["Account Currency"] || "USD";
        const amtNum = Number(fundAmount);
        const amtStr = isFinite(amtNum)
          ? amtNum.toLocaleString("en-US", { style: "currency", currency })
          : String(fundAmount);

        // Generate the formatted timestamp (e.g., "4:19 PM")
        const formattedTime = toAmPm(`${now.getHours()}:${now.getMinutes()}`);

        const fundingDoc = {
          "Trade #": Date.now(),                 
          "Date": formatDate(now),               
          "Product": "Funding",                  
          "Position": "",
          "Size": 0,
          "Win": false,
          "Loss": false,
          "Actual Percent Growth": "",
          "Actual Reward": 0,                    
          "Actual Balance": String(newBalance.toFixed(2)),
          "Entry Time": formattedTime, // <-- Inject the timestamp here
          "Exit Time": "",
          "Duration": "",
          "Notes": action === "add"
            ? `Deposit ${amtStr}`
            : `Withdrawal ${amtStr}`,
          "Links_Screenshots": "",

          // Flags/metadata (used by Journal to avoid counting in stats)
          "isFunding": true,
          "Funding Type": action === "add" ? "Deposit" : "Withdrawal",
          "Funding Amount": amtNum,
          "Funding Currency": currency,

          "created": new Date()
        };

        await appendTrade(userId, accountId, fundingDoc);
      } catch (e) {
        console.error("Failed to write funding row:", e);
        // Non-fatal: the balance is already updated; just skip the journal row if this fails
      }


      // Reflect immediately in UI
      setSettings(prev => ({
        ...prev,
        "Running Balance": newBalance,
        "Beginning Balance": newBalance, // <-- FIX: Instantly sync the UI state
        "Remaining Profit Amount": newRemaining,
      }));

      setFundMsg(action === "add" ? "Funds added successfully." : "Withdrawal applied successfully.");
      setFundAmount("");
    } catch (e) {
      console.error(e);
      setFundErr("Failed to update balance. Please try again.");
    } finally {
      setFunding(false);
    }
  }


  const makeTrapoundTradeId = (() => {
    const base = Date.now();
    let inc = 0;
    return () => base + (inc++);
  })();

  function getProduct(val) {
    if (!val) return "";
    if (val[0] === "M") return val.slice(0, 3);
    return val.slice(0, 2);
  }

  async function handleImport(e) {
    e.preventDefault();
      if (isMannyGoogleSheet && !googleSheetUrl) return;
      // For CSV platforms (TopStep, Tradovate, Trapound): only require platform + file
      if (!isMannyGoogleSheet && (!platform || !file)) return;
    
    setImporting(true);
    setImportLog([]);
    setImportDuplicates([]);

    let journalRows = [];
    try {
      journalRows = await fetchJournalRows(userId, accountId);
    } catch {
      setImportLog(l => [...l, "Could not fetch journal rows for duplicate check"]);
    }

        // MT5 takes its own path (CSV or XLSX)
        if (platform === "mt5") {
          await importMT5(file, journalRows);
          return;
        }
    
    if (isMannyGoogleSheet) {
        const spreadsheetId = googleSheetUrl.match(/\/d\/([a-zA-Z0-9_-]+)/)?.[1];
        if (!spreadsheetId) {
            setImportLog(l => [...l, "Invalid Google Sheet URL provided"]);
            setImporting(false);
            return;
        }

        const compoundPlanUrl = `https://docs.google.com/spreadsheets/d/${spreadsheetId}/gviz/tq?tqx=out:csv&sheet=Compound%20Plan`;
        const summaryUrl = `https://docs.google.com/spreadsheets/d/${spreadsheetId}/gviz/tq?tqx=out:csv&sheet=Summary`;

        try {
            const [compoundPlanResponse, summaryResponse] = await Promise.all([
              fetch(compoundPlanUrl),
              fetch(summaryUrl)
            ]);

            if (!compoundPlanResponse.ok || !summaryResponse.ok) {
              throw new Error("Failed to fetch one or both Google Sheet tabs. Please check sharing permissions.");
            }
            
            const compoundPlanCsv = await compoundPlanResponse.text();
            const summaryCsv = await summaryResponse.text();
            
            const summaryData = Papa.parse(summaryCsv).data;
            const allRows = Papa.parse(compoundPlanCsv, { header: false }).data;
            if (allRows.length < 2) throw new Error("Google Sheet 'Compound Plan' has no data rows.");
            
            const headers = allRows[0].map(h => h ? h.trim() : '');
            const colMap = headers.reduce((acc, header, index) => {
                if (header) acc[header] = index;
                return acc;
            }, {});
            const dataRows = allRows.slice(1);
            
            let imported = 0, skippedDuplicates = 0, skippedInvalid = 0, dupeArr = [];
            const accountSettings = await getAccountSettings(userId, accountId);
            const existingFingerprints = new Set(journalRows.map(row => row.importFingerprint).filter(Boolean));
            
            const currentSettings = accountSettings?.settings || {};
             // Recognize all prop flags
             const isPropFirm = Boolean(
               currentSettings.isPropFirm ||
               currentSettings.isProp ||
               currentSettings.isPropFunded
             );
            
            let runningBalance = currentSettings["Running Balance"] || 0;
            let propRunningBalance = currentSettings["Prop Running Balance"] || 0;
            
             // Decide whether the sheet's "Actual Balance (Edited by you)" is RB or PRB
             const firstSheetBalance = dataRows
               .map(r => parseSheetNumber(r[colMap["Actual Balance (Edited by you)"]]))
              .find(v => Number.isFinite(v));
            
             const useRBBaseline =
               !isPropFirm
                 ? true
                 : (firstSheetBalance != null &&
                    Math.abs(firstSheetBalance - runningBalance) <=
                    Math.abs(firstSheetBalance - propRunningBalance));
            
             // Seed from whichever baseline the sheet appears to be using
             let prevSheetBalance = useRBBaseline ? runningBalance : propRunningBalance;
             let seededFromBaseline = false;
            
            let settingsToUpdate = {
                "settings.Risk Percent":    parseSheetNumber(findValueByLabel(summaryData, "Risk Percent")),
                "settings.Take Profit":     parseSheetNumber(findValueByLabel(summaryData, "Take Profit")),
                "settings.Stop Loss":       parseSheetNumber(findValueByLabel(summaryData, "Stop Loss")),
            };

            if (!isPropFirm) {
                settingsToUpdate["settings.Account Goal"] = parseSheetNumber(findValueByLabel(summaryData, "Account Goal"));
                settingsToUpdate["settings.Initial Balance"] = parseSheetNumber(findValueByLabel(summaryData, "Initial Balance"));
            }

            for (const row of dataRows) {
                const currentSheetBalance = parseSheetNumber(row[colMap["Actual Balance (Edited by you)"]]);
                const dateString = row[colMap["Date"]];
                
                if (isNaN(currentSheetBalance) || !dateString) { skippedInvalid++; continue; }

                const product = row[colMap['Pair']] || 'N/A';
                const position = row[colMap['Position']] || 'N/A';
                const lotSize = row[colMap['Lot Size (Max Lot Size = 1000)']] || '0';
                const tradeDate = new Date(dateString);
                const fingerprint = `${formatDate(tradeDate)}-${product}-${position}-${lotSize}-${currentSheetBalance}`.toLowerCase();


                   if (existingFingerprints.has(fingerprint)) {
                     dupeArr.push(`Row for ${formatDate(tradeDate)} with balance ${currentSheetBalance}`);
                     skippedDuplicates++;
                     // IMPORTANT: advance baseline even when skipping
                     prevSheetBalance = currentSheetBalance;
                     seededFromBaseline = true;
                     continue;
                   }


                   // First non-duplicate row: delta from chosen baseline or prior sheet row
                   const actualReward = currentSheetBalance - prevSheetBalance;
                
                runningBalance += actualReward;
                propRunningBalance += actualReward;
                
                const totalRisk = Math.abs(parseSheetNumber(row[colMap['Potential Risk']]));
                
                 // Reward:Risk (e.g., 2:1). Use absolute values; Win/Loss is tracked separately.
                 const actualRiskToReward = (() => {
                   const r = Math.abs(totalRisk);
                   const R = Math.abs(actualReward);
                   if (r <= 0) return R > 0 ? "∞:1" : "0:1";
                   const left = Number((R / r).toFixed(2)).toString().replace(/\.?0+$/, "");
                   return `${left}:1`;
                 })();

                 const pickCell = (row, candidates) => {
                   for (const c of candidates) {
                     const k = Object.keys(row).find(
                       key => key && key.trim().toLowerCase() === c.trim().toLowerCase()
                     );
                     const v = k ? row[k] : undefined;
                     if (v != null && String(v).trim() !== "") return String(v).trim();
                   }
                   return "";
                 };



                // Helpers (inline here; or reuse your existing getPropPoolParts if available)
                const toNum = v => Number(v || 0);
                const safeDivPct = (num, den) => (den > 0 ? (num / den) * 100 : 0);

                // Works whether Maximum Loss Limit is entered as a loss amount (e.g., 5,000)
                // or as an absolute blown-at balance (e.g., 48,000 on a 50k account).
                function blownAtBalance(initialBalance, maximumLossLimit) {
                  const ib  = toNum(initialBalance);
                  const mll = toNum(maximumLossLimit);
                  return (ib > 0 && mll > 0) ? Math.max(mll, ib - mll) : 0; // 0 => non-prop
                }

                function getPropPoolParts(s) {
                  const ib = Number(
                    s?.["Initial Balance"] ??
                    s?.settings?.["Initial Balance"] ??
                    0
                  );
                  const mll = Number(
                    s?.["Maximum Loss Limit"] ??
                    s?.["Max Loss Limit"] ??
                    s?.settings?.["Maximum Loss Limit"] ??
                    s?.settings?.["Max Loss Limit"] ??
                    0
                  );

                  if (!(ib > 0 && mll > 0)) return null;

                  const blownAt   = Math.max(mll, ib - mll);
                  const poolStart = Math.max(0, ib - blownAt);
                  return { blownAt, poolStart };
                }

                function getEffectiveRiskBase(s) {
                  const parts = getPropPoolParts(s); // { blownAt, poolStart } when IB & MLL are valid
                  const rb = Number(s["Running Balance"] || 0);

                  if (parts) {
                    // How far above the blown-at balance are we right now?
                    return Math.max(0, rb - parts.blownAt);
                  }

                  // Fallbacks if IB/MLL not fully known
                  const mll = Number(s["Maximum Loss Limit"] || s["Max Loss Limit"] || 0);
                  if (mll > 0) return mll;  // conservative assumption
                  return Math.max(0, rb);   // last resort
                }

                // Compute the base to use for % calcs on this row.
                function baseForPercents({
                  prevLedgerRB,          // previous sheet "Running Balance" (your prevSheetBalance)
                  prevPropRB,            // previous "Prop Running Balance" if you have it in the sheet
                  initialBalance,        // from settings / sheet meta
                  maximumLossLimit       // from settings / sheet meta
                }) {
                  const ib        = toNum(initialBalance);
                  const mll       = toNum(maximumLossLimit);
                  const hasProp   = ib > 0 && mll > 0;
                  const prevRB    = toNum(prevLedgerRB);
                  const prevPRB   = toNum(prevPropRB);

                  if (!hasProp) return prevRB; // non-prop → keep current behavior

                  // Prefer explicit Prop RB if your sheet has it
                  if (prevPRB > 0) return prevPRB;

                  // Otherwise reconstruct prop pool for this row:
                  // propPool(t) = poolStart + cumulativePnL = (rb - blownAt)
                  const blownAt = blownAtBalance(ib, mll);
                  const reconstructed = Math.max(0, prevRB - blownAt);

                  // Fallback to initial pool if the reconstruction would be zero
                  const poolStart = Math.min(mll, Math.max(0, ib - mll)); // amount you can lose from start
                  return reconstructed > 0 ? reconstructed : poolStart;
                }

                // --- your existing context values ---
                // prevSheetBalance: the prior row's ledger Running Balance
                // totalRisk:        $ at risk on this trade
                // actualReward:     realized PnL for this trade
                // tradeDate:        Date for this row
                // row, colMap:      your import row + column map

                // If you have settings available in this scope:
                const ib  = toNum(settings?.["Initial Balance"]);
                const mll = toNum(settings?.["Maximum Loss Limit"]);

                // If your sheet includes "Prop Running Balance", pass it; otherwise leave undefined.
                const prevPropRBFromSheet = toNum(row[colMap["Prop Running Balance"]]);

                const base = baseForPercents({
                  prevLedgerRB: prevSheetBalance,
                  prevPropRB:   prevPropRBFromSheet,
                  initialBalance: ib,
                  maximumLossLimit: mll
                });



                // ✅ These two now auto‑switch: prop accounts use prop base; non‑prop use ledger base.
                const percentOfBalance = safeDivPct(totalRisk, base);                // e.g., 2.00 (%)
                const percentGrowth    = safeDivPct(actualReward, base);             // e.g., -0.45 (%)

                // If you store growth as a string with a "%" (consistent with the app’s checks):
                const percentGrowthStr = `${percentGrowth.toFixed(2)}%`;
                const tradeNumber = tradeDate.getTime();
                const thisNote = row[colMap['Notes']] || `Imported from Google Sheet on ${tradeDate.toISOString().slice(0, 10)}`;



                const beforeRB = runningBalance - actualReward;
                const tradeObj = {
                    "Actual Balance": String(runningBalance.toFixed(2)),
                    "Balance Before Trade": String(beforeRB.toFixed(2)),
                    "Actual Percent Growth": `${percentGrowth.toFixed(2)}%`,
                    "Actual Reward": String(actualReward.toFixed(2)),
                    "Actual Risk to Reward": actualRiskToReward,
                    "Date": formatDate(tradeDate),
                    "Did I follow my Rules?": "", "Duration": "",
                    "Entry Strategy": row[colMap['Strategy']] || "", "Entry Time": "",
                    "Exit Strategy": "", "Exit Time": "", "Grade": "", "Lesson": "",
                    "Links_Screenshots": "", "LogLesson": false,
                    "Loss": String(row[colMap['Loss']]).toLowerCase() === "true",
                    "Mood_Mindset": "",
                    "Notes": thisNote,
                    "Percent of Balance": Number(percentOfBalance.toFixed(2)),
                    "Position": position,
                    "Product": product, "Risk $": "0.00", "Session": "",
                    "Size": lotSize,
                    "Stop Loss_Take Profit": `${settingsToUpdate["settings.Stop Loss"]}/${settingsToUpdate["settings.Take Profit"]}`,
                    "Suggested Max # of Contracts": 0,
                    "Target Reward": "0.00",
                    "Total Risk": String(totalRisk.toFixed(2)),
                    "Trade #": tradeNumber,
                    // --- CRITICAL TYPO FIX ---
                    "Win": String(row[colMap['Win']]).toLowerCase() === "true",
                    "created": new Date(),
                    "importFingerprint": fingerprint,
                };
                await appendTrade(userId, accountId, tradeObj);
                   prevSheetBalance = currentSheetBalance;
                   seededFromBaseline = true;
                imported++;
            }
            
            if (imported > 0) {
              settingsToUpdate["settings.Running Balance"] = runningBalance;
              settingsToUpdate["settings.Prop Running Balance"] = propRunningBalance;
            }
            
            if (Object.keys(settingsToUpdate).length > 0) {
              await updateAccountSettings(userId, accountId, settingsToUpdate);
              setImportLog(l => [...l, `Account settings updated.`]);
            }

            setImportLog(l => [...l, `Import complete. Imported: ${imported}, Skipped Duplicates: ${skippedDuplicates}, Skipped Invalid: ${skippedInvalid}`]);
            if (dupeArr.length > 0) setImportDuplicates(dupeArr);

        } catch (error) {
            console.error("Error importing from Google Sheet:", error);
            let errorMessage = `Error importing: ${error.message}`;
            if (error.message.includes("Failed to fetch")) {
              errorMessage += `. Ensure that the sharing access for the Google Sheet was changed to "Anyone with the link".`;
            }
            setImportLog(l => [...l, errorMessage]);
        }
        setImporting(false);
        setGoogleSheetUrl("");

    } else {
       Papa.parse(file, {
          header: true,
          transformHeader: (h) =>
            String(h ?? "")
              .replace(/^\uFEFF/, "") // strip UTF‑8 BOM
              .replace(/\r/g, "")     // strip stray CRs
              .trim(),
         skipEmptyLines: true,
         complete: async (results) => {

           let imported = 0, skipped = 0, dupeArr = [];
            const alreadyNotes = journalRows.map((row) => (row.Notes || row.Note || row.note || "").toLowerCase());
            const accountSettings = await getAccountSettings(userId, accountId);
        
           // Helper: parse currency or numeric strings to numbers (no recalculation)
           const asNumber = (v) => parseSheetNumber(v); // uses the existing helper at top of file
            // Strict, punctuation-agnostic normalizer
            const nk = (s) => String(s ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
           
            // Map normalized header -> original header exactly as seen in the row
            const buildKeyIndex = (row) => {
              const m = new Map();
              for (const k of Object.keys(row || {})) m.set(nk(k), k);
              return m;
            };
           
            // Look up a cell by **exact normalized equality** to any alias (no "contains"!)
/*            const colStrict = (row, ...aliases) => {
              const idx = buildKeyIndex(row);
              for (const a of aliases) {
                const key = idx.get(nk(a));
                const v = key ? row[key] : undefined;
                if (v != null && String(v).trim() !== "") return String(v).trim();
              }
              return "";
            };*/
           
            // Numeric version (uses existing parse helper at top of file)
/*            const colNumStrict = (row, ...aliases) => {
              const s = colStrict(row, ...aliases);
              return parseSheetNumber(s);
            };
*/

            // ---- Column helpers (strict; no headerMap/normalizeKey globals) ----




            const colStrict = (row, ...aliases) => {
              const idx = buildKeyIndex(row);
              for (const a of aliases) {
                const key = idx.get(nk(a));
                const v = key ? row[key] : undefined;
                if (v != null && String(v).trim() !== "") return String(v).trim();
              }
              return "";
            };

            const colNumStrict = (row, ...aliases) => parseSheetNumber(colStrict(row, ...aliases));

            /**
             * Backward‑compatible wrapper used elsewhere in the file.
             * Tries strict match first, then a TF-specific fallback scan.
             */
           const col = (row, ...aliases) => {
              const s = colStrict(row, ...aliases);
              if (s) return s;

              // fallback for Timeframe-like headers (TF, TF (min), Time Frame, etc.)
              const fbKey = Object.keys(row).find((k) => {
                const n = nk(k);
                return n === "timeframe" || n === "tf" || n.startsWith("tf") || n.includes("timeframe");
              });
              return fbKey && row[fbKey] != null ? String(row[fbKey]).trim() : "";
            };

            const colNum = (row, ...aliases) => parseSheetNumber(col(row, ...aliases));

   
             const normalizeKey = (s) => String(s || "").replace(/[\s_-]+/g, "").toLowerCase();
             const headerMap = {};
             if (results?.meta?.fields?.length) {
               for (const f of results.meta.fields) headerMap[normalizeKey(f)] = f;
             }

             // Helper to get a column by a canonical name (with fallbacks)
/*             const col = (row, ...names) => {
               for (const n of names) {
                 const key = headerMap[normalizeKey(n)] || n;
                 const v = row[key];
                 if (v !== undefined && v !== null && String(v).trim() !== "") return String(v).trim();
               }
               return "";
             };

             // Numeric version (currency/number parsing only)
             const colNum = (row, ...names) => parseSheetNumber(col(row, ...names));*/

             // =============== TRAPOUND (READ-ONLY: take values directly from CSV) ===============
             if (platform === "trapound") {
               const existingFingerprints = new Set(
                 (journalRows || []).map(r => r.importFingerprint).filter(Boolean)
               );

               // Local helpers (scoped here to avoid name collisions with other importers)
               const tpNk = (s) => String(s ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
               const tpKey = (row, ...aliases) => {
                 const idx = new Map(Object.keys(row || {}).map(k => [tpNk(k), k]));
                 for (const a of aliases) {
                   const k = idx.get(tpNk(a));
                   const v = k ? row[k] : undefined;
                   if (v != null && String(v).trim() !== "") return String(v).trim();
                 }
                 return "";
               };
               // Money parser that ignores ratios like "8.8:1" and uses parseSheetNumber
               const tpMoney = (row, ...aliases) => {
                 const raw = tpKey(row, ...aliases);
                 if (!raw || raw.includes(":")) return NaN;       // ignore ratios
                 return parseSheetNumber(raw);
               };

               // Numeric cell reader for Ticks/Pips (uses the same parse helper)
               const tpNum = (row, ...aliases) => {
                 const raw = tpKey(row, ...aliases);
                 const n = parseSheetNumber(raw);
                 return Number.isFinite(n) ? n : null;
               };
               
               // Quick product-type detection for Trapound rows
               const FOREX_SET = new Set(FOREX_PAIRS.map(([v]) => v));
               const FUTURES_CODE_SET = new Set(FUTURES_PRODUCTS.map(([v]) => futuresCodeOf(v)));
               const isForexProduct = (prod) => {
                 const s = String(prod || "").trim();
                 if (!s) return false;
                 if (s.includes("/")) return true;             // e.g., "EUR/USD"
                 return FOREX_SET.has(s.replace("/", ""));     // "EURUSD"
               };
               const isFuturesProduct = (prod) => {
                 const code = String(getProduct(prod) || "").toUpperCase(); // e.g., "ES", "NQ", "YM"
                 return FUTURES_CODE_SET.has(code);
               };

               let lastActualBalance = null;
               let rowIndex = -1;

               for (let i = results.data.length - 1; i >= 0; i--) {
                   const r = results.data[i];
                 rowIndex++;
                 // Skip completely empty rows
                 if (!r || Object.values(r).every(v => String(v ?? "").trim() === "")) continue;

                 // Track final Actual Balance to update RB/PRB at the end
                 const rbFromRow = asNumber(r["Actual Balance"]);
                 if (Number.isFinite(rbFromRow)) lastActualBalance = rbFromRow;

                 // Win/Loss & reward normalization (keeps your existing behavior)
                 const resultStr = String(r["Result"] || "").trim().toLowerCase();
                 const win  = /^(won|win|true|1)$/i.test(String(r["Win"] || ""))  || resultStr === "won"  || resultStr === "win";
                 const loss = /^(loss|lost|true|1)$/i.test(String(r["Loss"] || "")) || resultStr === "loss" || resultStr === "lost";

                 let reward = asNumber(r["Actual Reward"]);
                 if (loss && reward > 0) reward = -Math.abs(reward);

                 const balanceBefore = asNumber(r["Actual Balance"]) - reward;

                 // --- Timeframe: take it directly from the file (no derivations)
                 let tf = tpKey(r, "Timeframe", "Time Frame", "TF", "TF (min)", "Timeframe (mins)");
                 if (!tf) {
                   // last chance: any header that effectively looks like 'tf'/'timeframe'
                   const k = Object.keys(r || {}).find(k => {
                     const n = tpNk(k);
                     return n === "timeframe" || n === "tf" || n.startsWith("tf") || n.startsWith("timeframe");
                   });
                   if (k && r[k] != null) tf = String(r[k]).trim();
                 }

                 // --- Total Risk: take it directly from the file (no computations, ignore ratios)
                 const riskVal = tpMoney(
                   r,
                   "Total Risk","Total Risk ($)","Risk Amount","Risk Amount ($)",
                   "Risk $","Risk ($)","Potential Risk","Potential Risk ($)"
                 );
                 const totalRisk = Number.isFinite(riskVal) ? riskVal : 0;

                 // One-time debug on first non-empty row
                 if (rowIndex === 0) {
                   console.log("[Trapound/DEBUG] headers:", Object.keys(r));
                   console.log("[Trapound/DEBUG] Timeframe:", tf);
                   console.log(
                     "[Trapound/DEBUG] TotalRisk raw:",
                     tpKey(r, "Total Risk","Risk Amount","Risk $","Risk ($)","Potential Risk","Potential Risk ($)"),
                     "parsed:", totalRisk
                   );
                 }

                 // Dedup fingerprint (stable across re-imports)
                 const fp = `${String(r["Date"] || "").trim()}|`
                          + `${String(r["Product"] || "").trim()}|`
                          + `${String(r["Position"] || "").trim()}|`
                          + `${String(r["Size"] || "").trim()}|`
                          + `${asNumber(r["Actual Balance"]) ?? ""}|${asNumber(r["Actual Reward"]) ?? ""}`
                          .toLowerCase();

                 if (existingFingerprints.has(fp)) continue;

                  // Read Ticks & Pips from CSV (try a few common header aliases)
                  const prodRaw = r["Product"] || "";
                  const ticksVal = tpNum(r, "Ticks", "Tick", "Ticks (Futures)");
                  const pipsVal  = tpNum(r, "Pips", "Pip", "Pips (Forex)");
                  const rowIsForex   = isForexProduct(prodRaw);
                  const rowIsFutures = isFuturesProduct(prodRaw);
                  const ticksOut = rowIsFutures ? (ticksVal ?? null) : null;
                  const pipsOut  = rowIsForex   ? (pipsVal  ?? null) : null;
                 
                  const tradeObj = {
                   "Trade #": makeTrapoundTradeId(),
                   "Product": r["Product"] || "",
                   "Position": r["Position"] || "",
                   "Size": r["Size"] || "",
                   "Actual Percent Growth": r["Actual Percent Growth"] || "",
                   "Result": r["Result"] || "",
                   "Actual Reward": reward,
                   "Actual Balance": asNumber(r["Actual Balance"]),
                   "Balance Before Trade": balanceBefore,
                   "Date": r["Date"] || "",
                   "Entry Time": r["Entry Time"] || "",
                   "Exit Time": r["Exit Time"] || "",
                   "Duration (h:mm)": r["Duration"] || r["Duration (h:mm)"] || "",
                   "Links_Screenshots": r["Links_Screenshots"] || "",
                   "Session": r["Session"] || "",
                   "Entry Strategy": r["Entry Strategy"] || "",
                   "Exit Strategy": r["Exit Strategy"] || "",
                   "Timeframe": tf,
                   "Time Frame": tf,                    // keep both keys for UI compatibility
                   "Did I follow my Rules?": r["Did I follow my Rules?"] || "",
                   "Grade": r["Grade"] || "",
                   "Mood_Mindset": r["Mood_Mindset"] || "",
                   "Stop Loss_Take Profit": r["Stop Loss_Take Profit"] || "",
                   "Total Risk": totalRisk,             // numeric
                   "Risk $": totalRisk.toFixed(2),      // optional display string
                   "Percent of Balance": asNumber(r["Percent of Balance"]),
                   "Actual Risk to Reward": r["Actual Risk to Reward"] || "",
                   "Notes": r["Notes"] || "",
                   "Lesson": r["Lesson"] || "",
                   "Win": !!win,
                   "Loss": !!loss,
                   "created": new Date(),
                   "importFingerprint": fp
                 };

                 // Only add keys when present (avoid polluting Firestore with nulls)
                 if (ticksOut !== null) tradeObj["Ticks"] = ticksOut;
                 if (pipsOut  !== null) tradeObj["Pips"]  = pipsOut;

                 await appendTrade(userId, accountId, tradeObj);
                 existingFingerprints.add(fp);
                 imported++;
               }

               // Update account balances from the file’s final Actual Balance
               if (Number.isFinite(lastActualBalance)) {
                 const currentSettings = accountSettings?.settings || {};
                 const isPropAcct = Boolean(
                   currentSettings.isProp || currentSettings.isPropFirm || currentSettings.isPropFunded
                 );

                 const startRB  = Number(currentSettings["Running Balance"] || 0);
                 const startPRB = Number(
                   currentSettings["Prop Running Balance"] ??
                   Math.max(
                     0,
                     Number(currentSettings["Initial Balance"] || 0) -
                     Number(currentSettings["Maximum Loss Limit"] || 0)
                   )
                 );
                 const delta = Number(lastActualBalance) - startRB;

                 const updates = { "settings.Running Balance": Number(lastActualBalance) };
                 if (isPropAcct) updates["settings.Prop Running Balance"] = round2(startPRB + delta);

                 await updateAccountSettings(userId, accountId, updates);
               }

               setImporting(false);
               setImportLog(l => [`Trapound import complete. Imported: ${imported}`, ...l]);
               if (dupeArr.length > 0) setImportDuplicates(dupeArr);
               return; // do not fall through to other platform logic
             }
             // ============= END TRAPOUND (READ-ONLY) =============

                // Fingerprints from prior imports (Manny/Trapound etc.)
                const existingFingerprints = new Set(
                  (journalRows || []).map(r => r.importFingerprint).filter(Boolean)
                );
            

            // Robust money parser (handles $, commas, and parentheses-as-negative)
            const money = (v) => {
              if (v == null || v === "") return 0;
              const s = String(v).trim();
              const neg = /^\(.*\)$/.test(s) || /^-/.test(s);
              const n = Number(s.replace(/[^\d.]/g, ""));
              return neg ? -Math.abs(n) : n;
            };

               
            
                // ====== TopStep / Tradovate path (revised to always add & calculate PRB) ======
                const getPnL  = (r) => money(r.PnL ?? r["P&L"] ?? r.pnl);
                const getFees = (r) => Math.abs(money(
                  r.Fees ?? r.Fee ?? r.Commissions ?? r.Commission ?? r["Total Fees"] ?? 0
                ));

                // Always start from the account’s current saved balances (do NOT back‑solve from Final Balance)
                const startRB  = Number(accountSettings?.settings?.["Running Balance"] || 0);
                const startPRB = Number(
                  accountSettings?.settings?.["Prop Running Balance"] ??
                  Math.max(
                    0,
                    Number(accountSettings?.settings?.["Initial Balance"] || 0) -
                    Number(accountSettings?.settings?.["Maximum Loss Limit"] || 0)
                  )
                );

                // Final Balance is optional and only applied to the account RB at the end
                const hasFinal       = typeof finalBalance === "string" && finalBalance.trim() !== "" && isFinite(Number(finalBalance));
                const finalRBNumber  = hasFinal ? Number(finalBalance) : NaN;


                let runningBalance = startRB;
                let propRunningBalance = startPRB; // local tracker (for clarity)
                let prevBalance = startRB;

            for (let row of results.data) {
              let tradeNum = Date.now() + imported;
              let actReward = 0, thisNote = "", tradeId = "";
              let baseTradeData = {};

              if (platform === "topstep") {
                tradeId = row.Id;
                if(!tradeId) continue;
                thisNote = `Imported from TopStep ${tradeId}`;
                if (alreadyNotes.some(n => n.includes(tradeId))) {
                  dupeArr.push(tradeId); skipped++; continue;
                }
                actReward = getPnL(row) - getFees(row);
                const entered = splitDateTime(row.EnteredAt);
                baseTradeData = {
                  Date: formatDate(entered.date), Product: getProduct(row.ContractName),
                  Position: row.Type === "Short" ? "Sell" : "Buy", Size: Number(row.Size || 0),
                  EntryTime: toAmPm(entered.time), ExitTime: toAmPm(splitDateTime(row.ExitedAt).time),
                  Duration: calculateDuration(row.EnteredAt, row.ExitedAt),
                }
              } else if (platform === "tradovate") {
                tradeId = `${row.buyFillId}:${row.sellFillId}`;
                if(!row.buyFillId || !row.sellFillId) continue;
                thisNote = `Imported from Tradovate with ${tradeId}`;
                if (alreadyNotes.some(n => n.includes(tradeId))) {
                  dupeArr.push(tradeId); skipped++; continue;
                }
                const pnlVal = (row.pnl || "").toString().replace(/[\$,()]/g, "");
                actReward = row.pnl?.includes("(") ? -Math.abs(Number(pnlVal)) : Number(pnlVal);
                const bought = splitDateTime(row.boughtTimestamp);
                const sold = splitDateTime(row.soldTimestamp);
                let entryTime = "", exitTime = "";
                if (row.buyFillId > row.sellFillId) {
                    entryTime = toAmPm(sold.time); exitTime = toAmPm(bought.time);
                } else {
                    entryTime = toAmPm(bought.time); exitTime = toAmPm(sold.time);
                }
                baseTradeData = {
                  Date: formatDate(bought.date), Product: getProduct(row.symbol),
                  Position: row.buyFillId > row.sellFillId ? "Sell" : "Buy", Size: Number(row.qty || 0),
                  EntryTime: entryTime, ExitTime: exitTime, Duration: row.duration || "",
                }
              }

              runningBalance += actReward;
              propRunningBalance += actReward;
              const balanceBefore = runningBalance - actReward;
              const percentGrowth = imported === 0 && prevBalance > 0 ? ((runningBalance - prevBalance) / prevBalance) * 100 : ((runningBalance - (runningBalance - actReward)) / (runningBalance - actReward || 1)) * 100;



 
              const tradeObj = {
                "Actual Balance": String(runningBalance.toFixed(2)),
                "Actual Percent Growth": percentGrowth.toFixed(2).replace(/\.?0+$/, "") + "%",
                "Actual Reward": actReward, "Actual Risk to Reward": "", "Date": baseTradeData.Date,
                "Balance Before Trade": Number(balanceBefore.toFixed(2)),
                "Did I follow my Rules?": "", "Duration (h:mm)": baseTradeData.Duration,
                "Entry Strategy": "", "Entry Time": baseTradeData.EntryTime,
                "Exit Strategy": "", "Exit Time": baseTradeData.ExitTime,
                "Grade": "", "Lesson": "", "Links_Screenshots": "", "Loss": actReward < 0,
                "Max Stop Loss Size for 1 Contract(s) (Ticks)": 0, "Mood_Mindset": "",
                "Notes": thisNote,
                "Percent of Balance": 0, "Position": baseTradeData.Position,
                "Potential Balance": "", "Price Per Tick": 0, "Product": baseTradeData.Product,
                "Risk To Reward Ratio": "", "Running Balance": String(runningBalance.toFixed(2)),
                "Session": "", "Size": baseTradeData.Size, "Stop Loss_Take Profit": "",
                "Suggested Max # of Contracts": baseTradeData.Size, "Ticks": "",
                "Total Risk": "", "Total Target Reward": 0, "Trade #": tradeNum,
                "Win": actReward >= 0, "created": new Date(), "tradeNum": tradeNum,
              };

              if(Object.keys(tradeObj).length > 0){
                  try {
                    await appendTrade(userId, accountId, tradeObj);
                    imported++;
                  } catch (err) {
                    setImportLog((l) => [...l, `Error importing row (${platform}): ${JSON.stringify(tradeObj)}`]);
                  }
              }
            }
             // Apply Final Balance (if provided) ONLY to account Running Balance.
             // Prop Running Balance must be CALCULATED from imported PnL (ignore Final Balance for PRB).
             const usedRB = hasFinal ? finalRBNumber : runningBalance;
             // PnL realized in the imported file set:
             const fileDelta = runningBalance - startRB;      // NOT based on Final Balance
             const finalPRB  = startPRB + fileDelta;

             const currentSettings = accountSettings?.settings || {};
             const isPropAcct = Boolean(currentSettings.isProp || currentSettings.isPropFirm || currentSettings.isPropFunded);

             const updates = { "settings.Running Balance": Number(usedRB) };

             const maxLossLimit = Number(currentSettings["Maximum Loss Limit"] || 0);

              if (isPropAcct) {
                updates["settings.Prop Running Balance"] = Number(finalPRB);
              }

             await updateAccountSettings(userId, accountId, updates);

             // (Optional, clearer log)
             setImportLog([ 
               `Imported: ${imported}, Skipped Duplicates: ${skipped}`,
               hasFinal
                 ? `Final Balance provided → set Running Balance = ${usedRB.toFixed(2)}; Prop Running Balance calculated = ${finalPRB.toFixed(2)}`
                 : `Set Running Balance from file = ${usedRB.toFixed(2)}; Prop Running Balance calculated = ${finalPRB.toFixed(2)}`
             ]);

            setImporting(false);
            setImportLog([`Imported: ${imported}, Skipped Duplicates: ${skipped}`]);
            setImportDuplicates(dupeArr);
          },
        });
    }
  }

  if (!userId || !accountId) {
    return (
      <div className="p-10 text-center text-gray-500 dark:text-gray-300">
        Please select a trading account to view settings.
      </div>
    );
  }
  if (loading) {
    return (
      <div className="p-10 text-center text-gray-500 dark:text-gray-300">
        Loading settings…
      </div>
    );
  }



  return (
    <div className="bg-gray-100 dark:bg-gray-900 min-h-screen pb-24">
      <div className="flex justify-center">
        <div className="w-full max-w-xl bg-white dark:bg-gray-800 shadow-xl rounded-2xl p-6 mt-10 border border-gray-100 dark:border-gray-700 space-y-6">

          {/* NEW: Flex container for the icon and header */}
          <div className="flex items-center space-x-2"> 
            <SettingsIcon className="w-6 h-6 text-blue-800 dark:text-blue-200" /> {/* Added color classes for consistency */}
            <h2 className="text-3xl font-extrabold text-blue-800 dark:text-blue-200">
              Settings
            </h2>
          </div>
         <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <legend className="px-2 text-lg font-bold text-blue-800 dark:text-blue-400">
              General Settings
            </legend>
            <div className="space-y-4">
              <Toggle
                label={`Theme: ${settings["Theme"] === "Dark" ? "Dark" : "Light"}`}
                checked={settings["Theme"] === "Dark"}
                onChange={val => handleSettingChange("Theme", val ? "Dark" : "Light")}
              />
              {!isBacktesting && (
                <Toggle
                  label={`Show Pretrade Checklist: ${settings["Pretrade Checklist"] === "Yes" ? "Yes" : "No"}`}
                  checked={settings["Pretrade Checklist"] === "Yes"}
                  onChange={val => handleSettingChange("Pretrade Checklist", val ? "Yes" : "No")}
                />
              )}

              {/* --- ADD PRIVACY MODE TOGGLE HERE --- */}
                  <Toggle
                    label="Privacy Mode (Hide Balances & % Growth)"
                    checked={settings["Privacy Mode"] === true}
                    onChange={val => handleSettingChange("Privacy Mode", val)}
                  />
                  {/* ------------------------------------ */}

              {/* --- START OF NEW SUBSCRIPTION BLOCK --- */}
              <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700 mt-4">
                <div>
                  <div className="text-base font-semibold text-gray-800 dark:text-gray-100">
                    Manage Subscription
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    View or update your plan and billing details.
                  </div>
                </div>
                <Link
                  to="/account"
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-lg shadow-md transition-colors focus:ring-2 focus:ring-blue-400 focus:outline-none"
                >
                  Manage
                </Link>
              </div>
              {/* --- END OF NEW SUBSCRIPTION BLOCK --- */}
              
            </div>
          </fieldset>

          {/* Notifications Settings */}
          <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <legend className="px-2 text-lg font-bold text-blue-800 dark:text-blue-400">
              Notifications
            </legend>
            <div className="space-y-4">
              <Toggle
                label="5 PM ET EOD Journaling Reminder"
                checked={settings["EOD Reminders"] === true}
                onChange={val => handleSettingChange("EOD Reminders", val)}
              />
              
              {settings["EOD Reminders"] === true && (
                <div className="space-y-4 pt-2">
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-gray-700 dark:text-gray-200">
                      Delivery Method
                    </div>
                    <select
                      value={settings["Reminder Method"] || "Email"}
                      onChange={(e) =>
                        handleReminderMethodSelection(e.target.value)
                      }
                      className="w-40 px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-400"
                    >
                      <option value="Email">Email</option>
                      <option value="Text">Text Message</option>
                    </select>
                  </div>

                  {/* NEW PHONE NUMBER FIELD */}
                  {settings["Reminder Method"] === "Text" && (
                    <div className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 p-3 dark:border-green-800 dark:bg-green-900/20">
                      <div>
                        <div className="text-sm font-semibold text-green-800 dark:text-green-300">
                          Text reminders enabled
                        </div>

                        <div className="text-xs text-green-700 dark:text-green-400">
                          {settings["SMS Consent Phone Number"] ||
                            phoneNumber}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={openSmsConsentModal}
                        className="text-sm font-semibold text-blue-600 underline hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        Update
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </fieldset>

           <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <legend className="px-2 text-lg font-bold text-blue-800 dark:text-blue-400">
                Dashboard Settings
            </legend>
            
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-gray-700 dark:text-gray-200">
                  Show Daily Account Summary
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Display table of all accounts on the dashboard.
                </div>
              </div>

              {/* Using your existing Toggle component */}
              <Toggle 
                label="" 
                checked={showDailySummary} 
                onChange={handleToggleDailySummary} 
              />
            </div>

          </fieldset>

           {!isBacktesting && (
           <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <legend className="px-2 text-lg font-bold text-blue-800 dark:text-blue-400">
                Trading Rules
            </legend>
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <label className="font-semibold text-gray-800 dark:text-gray-200">Max Trades Per Day</label>
                    <InfoTooltip content="We can get mentally fatigued after overtrading, consider setting a limit on the number of trades you take per day. Take the average number of trades that you take on your winning P&L days and consider using that number as your Max Trades Per Day." />
                  </div>
                  <div className="flex items-center gap-3">
                    <input type="number" value={settings["Max Trades Per Day"] ?? ""} onChange={e => handleSettingChange("Max Trades Per Day", e.target.value)}
                      className="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition" />
                    <Toggle label="" checked={settings["Max Trades Per Day Toggle"]} onChange={val => handleSettingChange("Max Trades Per Day Toggle", val)} />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                <label className="font-semibold text-gray-800 dark:text-gray-200">Max Loss Trades Per Day</label>
                <div className="flex items-center gap-3">
                    <input type="number" value={settings["Max Loss Trades Per Day"] ?? ""} onChange={e => handleSettingChange("Max Loss Trades Per Day", e.target.value)}
                    className="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"/>
                    <Toggle label="" checked={settings["Max Loss Trades Per Day Toggle"]} onChange={val => handleSettingChange("Max Loss Trades Per Day Toggle", val)}/>
                </div>
                </div>
                <div className="flex items-center justify-between">
                <label className="font-semibold text-gray-800 dark:text-gray-200">Drawdown Limit %</label>
                <div className="flex items-center gap-3">
                    <input type="number" value={settings["Drawdown Limit %"] ?? ""} onChange={e => handleSettingChange("Drawdown Limit %", e.target.value)}
                    className="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray.50 transition"/>
                    <Toggle label="" checked={settings["Drawdown Limit % Toggle"]} onChange={val => handleSettingChange("Drawdown Limit % Toggle", val)}/>
                </div>
                </div>
            </div>
           </fieldset>
           )}


          
          <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <legend className="px-2 text-lg font-bold text-blue-800 dark:text-blue-400">
              Trade Mode Settings
            </legend>
            <div className="space-y-4">
  
                <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <label className="font-semibold text-gray-800 dark:text-gray-200">Daily Goal</label>
                  <InfoTooltip content="Tip for setting the Daily Goal is to take the average of your winning P&L days and use that as your Daily Goal." />
                </div>
                <input type="number" value={settings["Daily Goal"] ?? ""} onChange={e => handleSettingChange("Daily Goal", Number(e.target.value))}
                  className="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition" />
              </div>             
              <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <label className="font-semibold text-gray-800 dark:text-gray-200">Daily Loss Limit</label>
                                <InfoTooltip content="You don't want to allow yourself to get too far into a hole. Never lose more than you know you can make in a day, set the Daily Loss Limit for that amount." />
                              </div>
                              <input type="number" value={settings["Daily Loss Limit"] ?? ""} onChange={e => handleSettingChange("Daily Loss Limit", Number(e.target.value))}
                                className="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition" />
                            </div>
              <div className="flex items-center justify-between hidden">
                <div className="flex items-center">
                  <label className="font-semibold text-gray-800 dark:text-gray-200">
                    {isFuturesMode
                      ? "Daily Tick Goal"
                      : isForexMode
                      ? "Daily Pip Goal"
                      : isStockMode
                      ? "Daily Point Goal"
                      : "Daily Goal"}
                  </label>
                  <InfoTooltip content="This goal will be used on the Win Projection table to project the number of trading days to reach your goal if you target a certain number of ticks/pips/points per day." />
                </div>
                <input type="number" value={settings["Daily Tick Goal"] ?? ""} onChange={e => handleSettingChange("Daily Tick Goal", Number(e.target.value))}
                  className="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition" />
              </div>               
          

              {(isFuturesMode || isOptionsMode || isMixedMode) && (
                <>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <label className="font-semibold text-gray-800 dark:text-gray-200">Maximum Contracts</label>
                      <InfoTooltip content="If you prefer to always trade the same number of contracts, set the Maximum Contracts and Minimum Contracts to the same number." />
                    </div>
                    <input type="number" value={settings["Maximum Contracts"] ?? ""} onChange={e => handleSettingChange("Maximum Contracts", e.target.value)}
                      className="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition" />
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="font-semibold text-gray-800 dark:text-gray-200">Minimum Contracts</label>
                    <input type="number" value={settings["Minimum Contracts"] ?? ""} onChange={e => handleSettingChange("Minimum Contracts", e.target.value)}
                    className="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"/>
                  </div>
                </>
              )}

              {/* --- ADD THIS NEW BLOCK FOR STOCKS --- */}
              {(isStockMode || isMixedMode) && (
                <>
                  <div className="flex items-center justify-between hidden">
                    <div className="flex items-center">
                      <label className="font-semibold text-gray-800 dark:text-gray-200">Maximum Shares</label>
                      <InfoTooltip content="Set the maximum number of shares you want to trade per position." />
                    </div>
                    <input type="number" value={settings["Maximum Shares"] ?? ""} onChange={e => handleSettingChange("Maximum Shares", e.target.value)}
                      className="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition" />
                  </div>
                  <div className="flex items-center justify-between hidden">
                    <div className="flex items-center">
                      <label className="font-semibold text-gray-800 dark:text-gray-200">Minimum Shares</label>
                      <InfoTooltip content="Set the minimum number of shares (e.g. 0.0001 for fractional). Loss projection stops when you can no longer afford this amount." />
                    </div>
                    <input type="number" step="0.0001" value={settings["Minimum Shares"] ?? ""} onChange={e => handleSettingChange("Minimum Shares", e.target.value)}
                      className="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition" />
                  </div>
                </>
              )}

              {(isForexMode || isMixedMode) && (
                <>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <label className="font-semibold text-gray-800 dark:text-gray-200">
                        Max Lot Size
                      </label>
                      <InfoTooltip content="If you prefer to always trade the same lot size, set the Maximum Lot Size and Minimum Lot Size to the same number." />
                    </div>

                    <input
                      type="number"
                      step="0.01"
                      value={settings["Max Lot Size"] ?? ""}
                      onChange={e => handleSettingChange("Max Lot Size", e.target.value)}
                      className="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="font-semibold text-gray-800 dark:text-gray-200">
                      Min Lot Size
                    </label>

                    <input
                      type="number"
                      step="0.01"
                      value={settings["Min Lot Size"] ?? ""}
                      onChange={e => handleSettingChange("Min Lot Size", e.target.value)}
                      className="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"
                    />
                  </div>

                  <div className="flex items-center justify-between hidden">
                    <label className="font-semibold text-gray-800 dark:text-gray-200">
                      Account Currency
                    </label>

                    <select
                      value={settings["Account Currency"] ?? "USD"}
                      onChange={e => handleSettingChange("Account Currency", e.target.value)}
                      className="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"
                    >
                      {CURRENCY_OPTIONS.map(cur => (
                        <option key={cur} value={cur}>{cur}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-center justify-between hidden">
                    <label className="font-semibold text-gray-800 dark:text-gray-200">
                      Leverage
                    </label>

                    <select
                      value={settings["Leverage"] ?? 100}
                      onChange={e => handleSettingChange("Leverage", e.target.value)}
                      className="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"
                    >
                      {LEVERAGE_OPTIONS.map(lv => (
                        <option key={lv} value={lv}>{lv}:1</option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              <div className="flex items-center justify-between hidden">
                <div>
                  <div className="font-semibold text-gray-800 dark:text-gray-200">
                    Price‑Price based calculations
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    When enabled, suggested contracts are sized from entry price and price per tick
                    instead of stop‑loss ticks.
                  </div>
                </div>
                <Toggle
                  label=""
                  checked={!!settings["Calculation by Entry Price"]}
                  onChange={val => handleSettingChange("Calculation by Entry Price", val)}
                />
              </div>
            </div>
          </fieldset>


 {/* Journal Defaults & Visibility */}
 <CollapsibleCard title="Journal Defaults & Visibility" storageKey="tp:defaultsOpen" defaultOpen={false}>

  {/* NEW CALCULATOR ONLY TOGGLE */}
              
                <div>
                  <div className="font-medium text-gray-700 dark:text-gray-200">
                    Calculator Only Mode
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Hide the Journal Entry section on the Entry page.
                  </div>
                </div>
                <Toggle 
                  label="" 
                  checked={settings["Calculator Only"] === true} 
                  onChange={val => handleSettingChange("Calculator Only", val)} 
                />
                <div className="flex items-center justify-between border-t border-gray-100 dark:border-gray-700 pt-4">
              </div>


   <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
     Customize your journal entry form. Set default values for common fields or hide fields you don't use.
   </p>



   {/* Define the options list here */}
   <datalist id="settings-timeframe-options">
     {TIMEFRAME_OPTIONS.map(tf => (
       <option key={tf} value={tf} />
     ))}
   </datalist>

   <div className="space-y-1">
    {/* ✅ SESSION MOVED HERE with Options */}
        <FieldConfigRow
          label="Session"
          defaultKey="Default Session"
          hideKey="Hide Session"
          settings={settings}
          onChange={handleSettingChange}
          options={["Asian", "London", "New York", "Mixed"]}
        />


     {/* 1. Fields with Default Value + Hide Toggle */}
     <FieldConfigRow
       label="Entry Strategy"
       defaultKey="Default Entry Strategy"
       hideKey="Hide Entry Strategy"
       settings={settings}
       onChange={handleSettingChange}
     />
     <FieldConfigRow
       label="Exit Strategy"
       defaultKey="Default Exit Strategy"
       hideKey="Hide Exit Strategy"
       settings={settings}
       onChange={handleSettingChange}
     />

     {/* 2. Default Timeframe (Always Visible + Datalist Connected) */}
     <FieldConfigRow
       label="Timeframe"
       defaultKey="Default Timeframe"
       hideKey="" // No hide key needed
       settings={settings}
       onChange={handleSettingChange}
       showHideToggle={false}
       list="settings-timeframe-options" // ✅ Connects to the datalist ID above
     />

     {/* 3. Fields with Hide Toggle Only (No Default Value) */}
      <FieldConfigRow label="Entry Price" hideKey="Hide Entry Price" settings={settings} onChange={handleSettingChange} showDefaultInput={false} />
     <FieldConfigRow label="Exit Price" hideKey="Hide Exit Price" settings={settings} onChange={handleSettingChange} showDefaultInput={false} />
     
     <FieldConfigRow 
       label={isFuturesMode ? "Potential Ticks" : (isForexMode ? "Potential Pips" : "Potential Ticks")} 
       hideKey="Hide Potential" 
       settings={settings} 
       onChange={handleSettingChange} 
       showDefaultInput={false} 
     />
     
     <FieldConfigRow label="Ideal Stop Loss" hideKey="Hide Ideal Stop Loss" settings={settings} onChange={handleSettingChange} showDefaultInput={false} />
     <FieldConfigRow label="Mood" hideKey="Hide Mood" settings={settings} onChange={handleSettingChange} showDefaultInput={false} />
     <FieldConfigRow label="Grade" hideKey="Hide Grade" settings={settings} onChange={handleSettingChange} showDefaultInput={false} />
     <FieldConfigRow label="Lesson" hideKey="Hide Lesson" settings={settings} onChange={handleSettingChange} showDefaultInput={false} />
   </div>
 </CollapsibleCard>



  {!isStockMode && (
          <CollapsibleCard title="Favorite Products" storageKey="tp:favoritesOpen" defaultOpen={false}>
             <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
               Check the items you want pinned at the top of the selector on your Calculate page.
             </p>

             <div className="space-y-6">
               {/* Forex checklist */}
              {(isForexMode || isMixedMode) && (

                               <div>
                                 <div className="flex items-center justify-between mb-2">
                                   <h4 className="font-semibold text-gray-800 dark:text-gray-200">Forex Pairs & Indices</h4>
                                   <div className="text-xs flex gap-4">
                                     <button onClick={selectAllForex}
                                       className="underline underline-offset-4 decoration-2 text-blue-600 hover:text-blue-700
                                                  dark:text-blue-400 dark:hover:text-blue-300">Select all</button>
                                     <button onClick={clearAllForex}
                                       className="underline underline-offset-4 decoration-2 text-red-600 hover:text-red-700
                                                  dark:text-red-400 dark:hover:text-red-300">Clear</button>
                                   </div>
                                 </div>

                                 <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                                   {/* Combine Pairs and Indices here */}
                                   {[...FOREX_PAIRS, ...FOREX_INDICES].map(([value,label])=>(
                                     <label key={value}
                                            className="inline-flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200
                                                       rounded px-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-700">
                                       <input type="checkbox"
                                              checked={faveForex.has(value)}
                                              onChange={()=>toggleForex(value)} />
                                       <span className="truncate">{label}</span>
                                     </label>
                                   ))}
                                 </div>
                               </div>
                             )}
               {/* Futures checklist */}
               {(isFuturesMode || isMixedMode) && (
                 <div>
                   <div className="flex items-center justify-between mb-2">
                     <h4 className="font-semibold text-gray-800 dark:text-gray-200">Futures Products</h4>
                     <div className="text-xs flex gap-4">
                       <button onClick={selectAllFut}
                         className="underline underline-offset-4 decoration-2 text-blue-600 hover:text-blue-700
                                    dark:text-blue-400 dark:hover:text-blue-300">Select all</button>
                       <button onClick={clearAllFut}
                         className="underline underline-offset-4 decoration-2 text-red-600 hover:text-red-700
                                    dark:text-red-400 dark:hover:text-red-300">Clear</button>
                     </div>
                   </div>

                   <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-2">
                     {FUTURES_PRODUCTS.map(([value,label])=>{
                       const code=futuresCodeOf(value);
                       return(
                         <label key={value}
                                className="inline-flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200
                                           rounded px-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-700"
                                title={label}>
                           <input type="checkbox"
                                  checked={faveFutures.has(code)}
                                  onChange={()=>toggleFutures(value)} />
                           <span className="truncate">{label}</span>
                         </label>
                       );
                     })}
                   </div>
                 </div>
               )}
             </div>

             {/* Save row */}
             <div className="mt-6 flex items-center gap-3">
               <button type="button" onClick={handleSaveFavorites} disabled={savingFavorites}
                 className={`inline-flex items-center px-4 py-2 rounded-lg font-semibold text-white
                            ${savingFavorites?"bg-blue-400":"bg-blue-600 hover:bg-blue-700"}`}>
                 {savingFavorites?"Saving...":"Save Favorites"}
               </button>
               {favoritesMsg&&<span className="text-sm text-gray-600 dark:text-gray-300">{favoritesMsg}</span>}
             </div>
           </CollapsibleCard>
)}

{showFundingSection && (
  <CollapsibleCard
    title={isPropFunded ? "Withdrawals" : "Account Funding"}
    storageKey="tp:fundingOpen"
    defaultOpen={false}
  >
    <div className="space-y-4">
      {/* Current Balance */}
      <div className="flex items-center justify-between">
        <label className="font-semibold text-gray-800 dark:text-gray-200">
          Current Balance
        </label>
        <div className="text-gray-900 dark:text-gray-100">
          {formatCurrency(runningBalance, accountCurrency)}
        </div>
      </div>

      {/* Amount Input */}
      <div className="flex items-center justify-between">
        <label className="font-semibold text-gray-800 dark:text-gray-200">
          Amount
        </label>
        <input
          type="number"
          step="0.01"
          value={fundAmount}
          onChange={e => setFundAmount(e.target.value)}
          placeholder="e.g., 250.00"
          className="w-40 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700
                     focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white
                     bg-gray-50 transition"
        />
      </div>

      {/* Buttons */}
      <div
        className={`grid ${isPropFunded ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2"} gap-3 pt-2`}
      >
        {!isPropFunded && (
          <button
            type="button"
            disabled={funding || !isFinite(amtNum) || amtNum <= 0}
            onClick={() => applyFunding(Math.abs(amtNum), "add")}
            className="w-full bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg
                       font-bold disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            Add Funds
          </button>
        )}
        <button
          type="button"
          disabled={funding || !isFinite(amtNum) || amtNum <= 0}
          onClick={() => applyFunding(-Math.abs(amtNum), "withdraw")}
          className="w-full bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg
                     font-bold disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          Withdraw
        </button>
      </div>

      {/* Previews */}
      <div className="text-sm text-gray-600 dark:text-gray-300 pt-2">
        {!isPropFunded && (
          <div className="flex items-center justify-between">
            <span>Balance after <b>Add</b>:</span>
            <span className="font-semibold">
              {formatCurrency(previewAdd, accountCurrency)}
            </span>
          </div>
        )}
        <div className="flex items-center justify-between">
          <span>Balance after <b>Withdraw</b>:</span>
          <span className="font-semibold">
            {formatCurrency(previewWithdraw, accountCurrency)}
          </span>
        </div>
      </div>

      {/* Messages */}
      {fundMsg && (
        <div className="mt-2 p-2 rounded bg-green-50 dark:bg-green-900/30
                        text-green-700 dark:text-green-300 text-sm">
          {fundMsg}
        </div>
      )}
      {fundErr && (
        <div className="mt-2 p-2 rounded bg-red-50 dark:bg-red-900/30
                        text-red-700 dark:text-red-300 text-sm">
          {fundErr}
        </div>
      )}
    </div>
  </CollapsibleCard>
)}

          


          {/* Copy Trade */}
{hasMultipleForex && (
  <CollapsibleCard title="Copy Trade — Forex" storageKey="tp:copyForexOpen">
    {/* --- ADD INDICATOR HERE --- */}
    {isTrialing && (
        <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center gap-2">
          <FaExclamationTriangle className="text-blue-600 dark:text-blue-400" />
          <span className="text-sm font-bold text-blue-800 dark:text-blue-200">
            Available after free trial
          </span>
        </div>
      )}
    {/* The Content is dimmed and non-clickable during trial */}
  <div className={isTrialing ? "opacity-50 pointer-events-none select-none" : ""}>
    <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
      Configure copy trading by choosing <strong>one</strong> Leader and any number of Followers.
      Trades only copy within the same account type.
    </p>

    <div className="mb-4 flex items-center gap-3">
      <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Enable copy trading</span>
      <button type="button" role="switch" aria-checked={copyEnabledForex}
              onClick={() => { 
                setCopyEnabledForex(!copyEnabledForex);
                setCopyTradeMsg("");
              }}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full transition
                          ${copyEnabledForex?"bg-indigo-600":"bg-gray-300 dark:bg-gray-600"}`}
              title={copyEnabledForex?"On":"Off"}>
        <span className={`pointer-events-none absolute top-0.5 left-0.5 h-5 w-5 transform rounded-full
                          bg-white shadow ring-0 transition
                          ${copyEnabledForex?"translate-x-5":"translate-x-0"}`} />
      </button>
      <span className="text-xs text-gray-600 dark:text-gray-400">
        {copyEnabledForex?"On":"Off"}
      </span>
    </div>

    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500 dark:text-gray-300 border-b dark:border-gray-700">
            <th className="py-2 pr-4">Account</th>
            <th className="py-2 pr-4">Leader</th>
            <th className="py-2 pr-4">Follower</th>
          </tr>
        </thead>
        <tbody>
          {[...forexAccounts].sort((a, b) => a.name.localeCompare(b.name)).map(a=>(
            <tr key={a.id} className="border-b last:border-0 dark:border-gray-700">
              <td className="py-2 pr-4 text-gray-900 dark:text-gray-100">{a.name}</td>
              <td className="py-2 pr-4">
                <input type="radio" name="forex-leader"
                       checked={fxLeaderId===a.id}
                       onChange={()=>handlePickFxLeader(a.id)} />
              </td>
              <td className="py-2 pr-4">
                <input type="checkbox" disabled={fxLeaderId===a.id}
                       checked={fxFollowerIds.has(a.id)}
                       onChange={()=>handleToggleFxFollower(a.id)} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>


    <div className="mt-6 flex items-center gap-3">
      <button type="button" onClick={handleSaveCopyTradeForex} disabled={savingCopyTrade}
        className={`inline-flex items-center px-4 py-2 rounded-lg font-semibold text-white
                    ${savingCopyTrade?"bg-indigo-400":"bg-indigo-600 hover:bg-indigo-700"}`}>
        <FaSave className="mr-2" />{savingCopyTrade?"Saving...":"Save"}
      </button>
      {copyTradeMsg&&<span className="text-sm text-gray-600 dark:text-gray-300">{copyTradeMsg}</span>}
    </div>
  </div>
  </CollapsibleCard>
)}


        {hasMultipleFutures && (
          <CollapsibleCard title="Copy Trade — Futures" storageKey="tp:copyFuturesOpen">
            {/* --- ADD INDICATOR HERE --- */}
            {isTrialing && (
                <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center gap-2">
                  <FaExclamationTriangle className="text-blue-600 dark:text-blue-400" />
                  <span className="text-sm font-bold text-blue-800 dark:text-blue-200">
                    Available after free trial
                  </span>
                </div>
              )}
            {/* The Content is dimmed and non-clickable during trial */}
            <div className={isTrialing ? "opacity-50 pointer-events-none select-none" : ""}>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              Configure copy trading by choosing <strong>one</strong> Leader and any number of Followers.
              Trades only copy within the same account type.
            </p>

            <div className="mb-4 flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Enable copy trading</span>
              <button type="button" role="switch" aria-checked={copyEnabledFutures}
                      onClick={() => {
                        setCopyEnabledFutures(!copyEnabledFutures);
                        setCopyTradeMsg("");
                      }}
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full transition
                                  ${copyEnabledFutures?"bg-indigo-600":"bg-gray-300 dark:bg-gray-600"}`}
                      title={copyEnabledFutures?"On":"Off"}>
                <span className={`pointer-events-none absolute top-0.5 left-0.5 h-5 w-5 transform rounded-full
                                  bg-white shadow ring-0 transition
                                  ${copyEnabledFutures?"translate-x-5":"translate-x-0"}`} />
              </button>
              <span className="text-xs text-gray-600 dark:text-gray-400">
                {copyEnabledFutures?"On":"Off"}
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 dark:text-gray-300 border-b dark:border-gray-700">
                    <th className="py-2 pr-4">Account</th>
                    <th className="py-2 pr-4">Leader</th>
                    <th className="py-2 pr-4">Follower</th>
                  </tr>
                </thead>
                <tbody>
                  {[...futuresAccounts].sort((a, b) => a.name.localeCompare(b.name)).map(a=>(
                    <tr key={a.id} className="border-b last:border-0 dark:border-gray-700">
                      <td className="py-2 pr-4 text-gray-900 dark:text-gray-100">{a.name}</td>
                      <td className="py-2 pr-4">
                        <input type="radio" name="futures-leader"
                               checked={leaderId===a.id}
                               onChange={()=>handlePickLeader(a.id)} />
                      </td>
                      <td className="py-2 pr-4">
                        <input type="checkbox" disabled={leaderId===a.id}
                               checked={followerIds.has(a.id)}
                               onChange={()=>handleToggleFollower(a.id)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-6 flex items-center gap-3">
              <button type="button" onClick={handleSaveCopyTradeFutures} disabled={savingCopyTrade}
                className={`inline-flex items-center px-4 py-2 rounded-lg font-semibold text-white
                            ${savingCopyTrade?"bg-indigo-400":"bg-indigo-600 hover:bg-indigo-700"}`}>
                <FaSave className="mr-2" />{savingCopyTrade?"Saving...":"Save"}
              </button>
              {copyTradeMsg&&<span className="text-sm text-gray-600 dark:text-gray-300">{copyTradeMsg}</span>}
            </div>
          </div>
          </CollapsibleCard>
        )}



      

</div>
      </div>
    {smsModalOpen && (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
    role="dialog"
    aria-modal="true"
    aria-labelledby="sms-consent-title"
  >
    <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl dark:bg-gray-800">
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h3
            id="sms-consent-title"
            className="text-xl font-bold text-gray-900 dark:text-white"
          >
            Enable Text Reminders
          </h3>

          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            Enter your mobile number and provide consent
            before text reminders are enabled.
          </p>
        </div>

        <button
          type="button"
          onClick={closeSmsConsentModal}
          disabled={savingSmsConsent}
          className="rounded-lg px-2 py-1 text-2xl leading-none text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50 dark:hover:bg-gray-700 dark:hover:text-gray-200"
          aria-label="Close text reminder consent dialog"
        >
          &times;
        </button>
      </div>

      <div className="space-y-5">
        <div>
          <label
            htmlFor="sms-phone-number"
            className="mb-2 block text-sm font-semibold text-gray-800 dark:text-gray-200"
          >
            Mobile Phone Number
          </label>

          <input
            id="sms-phone-number"
            type="tel"
            autoComplete="tel"
            placeholder="+15551234567"
            value={pendingPhoneNumber}
            onChange={(e) => {
              setPendingPhoneNumber(e.target.value);
              setPendingSmsConsent(false);
              setSmsModalError("");
            }}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-400 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          />

          <p
            className={`mt-2 text-xs ${
              pendingPhoneNumber && !isValidSmsPhone
                ? "text-red-600 dark:text-red-400"
                : "text-gray-500 dark:text-gray-400"
            }`}
          >
            Include the country code, such as
            +15551234567.
          </p>
        </div>

        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-700/50">
          <label
            className={`flex items-start gap-3 ${
              isValidSmsPhone
                ? "cursor-pointer"
                : "cursor-not-allowed"
            }`}
          >
            <input
              type="checkbox"
              checked={pendingSmsConsent}
              onChange={(e) =>
                setPendingSmsConsent(e.target.checked)
              }
              disabled={
                !isValidSmsPhone || savingSmsConsent
              }
              className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
            />

            <span className="block flex-1 space-y-3 text-[15px] leading-6 text-gray-700 dark:text-gray-200">
              <span className="block">
                I agree to receive recurring automated trade-journal
                reminder text messages from Trapound at the mobile
                number provided. Up to 1 reminder message per day.
              </span>

              <span className="block">
                <strong>Message and data rates may apply.</strong>{" "}
                Reply <strong>STOP</strong> to unsubscribe or{" "}
                <strong>HELP</strong> for help.
              </span>

              <span className="block rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
                If you previously replied <strong>STOP</strong>, you
                must text <strong>START</strong> to{" "}
                <a
                  href="sms:+14472344450"
                  className="font-semibold underline hover:no-underline"
                >
                  +1 (447) 234-4450
                </a>{" "}
                before text reminders can resume.
              </span>

              <span className="block">
                Consent is not a condition of purchase. Review the{" "}
                <a
                  href="https://trapound.net/legal/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-blue-600 underline hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Terms &amp; Conditions
                </a>{" "}
                and{" "}
                <a
                  href="https://trapound.net/privacy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-blue-600 underline hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Privacy Policy
                </a>
                .
              </span>
            </span>
          </label>
        </div>

        {smsModalError && (
          <p className="text-sm font-medium text-red-600 dark:text-red-400">
            {smsModalError}
          </p>
        )}

        <div className="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
          <button
            type="button"
            onClick={closeSmsConsentModal}
            disabled={savingSmsConsent}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleConfirmSmsConsent}
            disabled={
              !isValidSmsPhone ||
              !pendingSmsConsent ||
              savingSmsConsent
            }
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300 dark:disabled:bg-blue-900"
          >
            {savingSmsConsent
              ? "Enabling..."
              : "Enable Text Reminders"}
          </button>
        </div>
      </div>
    </div>
  </div>
)}
       {/* Floating Calculator */}
      <button onClick={() => setShowCalculator(true)} className="fixed bottom-12 right-6 z-40 bg-gray-800 text-white p-4 rounded-full shadow-lg hover:scale-110 transition">
        <FaCalculator />
      </button>
      {showCalculator && <SimpleCalculator onClose={() => setShowCalculator(false)} />}
    </div>
  );
}
